import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogTrigger, DialogTitle, DialogHeader } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Send, Image, AlertCircle, Camera, CheckCircle2, XCircle, Upload, Clock, ClipboardList, Loader2, Info, ExternalLink, Video, ClipboardCheck } from "lucide-react";
import { useChecklistForCleaner, useSubmitCleaning } from "@/hooks/useCleaner";
import { useReferencePhotos } from "@/hooks/useReferencePhotos";
import { useTaskReferencePhotos, getPhotosForTask } from "@/hooks/useTaskReferencePhotos";
import { usePropertyGuideEntries } from "@/hooks/usePropertyGuideEntries";
import { useCleaningProgress } from "@/hooks/useCleaningProgress";
import { useLatestSubmissionForTask } from "@/hooks/useCleaningSubmissions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Room, Task, getTaskText, normalizeTask, getTaskType, getTaskCleaningCategory, getMaintenanceSettings, getQuickCheckSettings, getCleaningSettings, isCleaningTask, getGuestRestockQuantity } from "@/types/checklist";
import type { CleaningCategory } from "@/types/checklist";
import { CLEANING_CATEGORY_LABELS } from "@/types/checklist";
import { useTranslation } from "@/hooks/useTranslation";
import { useDynamicChecklistTranslation } from "@/hooks/useDynamicChecklistTranslation";
import { TaskReferencePhotoStrip } from "./TaskReferencePhotoCarousel";
import CleanerPhotoUploadGallery from "./CleanerPhotoUploadGallery";
import { MaintenanceResponseValue } from "./MaintenanceTaskUI";
import { ChecklistRoomRow } from "./ChecklistRoomRow";
import { ChecklistTaskRow } from "./ChecklistTaskRow";
import PropertyGuideCard from "./PropertyGuideCard";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import { useInventoryItems } from "@/hooks/useInventory";
import { useSubmitInventory } from "@/hooks/useCleaner";
import { useTurnoverPurchases } from "@/hooks/useCleanerPurchases";
import InventoryItemSheet from "./InventoryItemSheet";
import { ProofStandardHostDetailContent, UnitItemProofTypeBadge } from "@/components/cleaner/ProofStandardHostDetailContent";
import type { RoomInventoryItem } from "./RoomInventoryPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { scrollRootToTop } from "@/lib/scrollRootToTop";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { compareCleaningPhotosForSubmission } from "@/lib/compareCleaningPhotos";
import type { Json } from "@/integrations/supabase/types";
import {
  useProofStandardsForChecklist,
  useUpsertStandardProofUpload,
  type ProofStandard,
} from "@/hooks/useProofStandards";
import { parseProofMediaByStandardIdFromEntryState, parseFunctionalPassStandardIdsFromEntryState } from "@/types/lifecycle";
import {
  isProofStandardFullySatisfied,
  mergeProofMediaByStandardForSubmission,
} from "@/lib/unitCheckProofKeys";
import { sendMaintenanceIssuesEmail } from "@/lib/notificationEmails";

/** Workflow chips + list: guest amenities are handled in the guest-amenity / staging flow, not here. Beds & linens merge into laundry. */
const WORKFLOW_VIEW_CATEGORY_KEYS = (Object.keys(CLEANING_CATEGORY_LABELS) as CleaningCategory[]).filter(
  (c) => c !== "auto" && c !== "beds_linens" && c !== "guest_amenities"
);

const generateUUID = () => {
  return crypto.randomUUID();
};

const sameSet = (a: Set<string>, bArr: string[]) => {
  if (a.size !== bArr.length) return false;
  for (const x of bArr) if (!a.has(x)) return false;
  return true;
};

export type IssueSeverity = "low" | "medium" | "high" | "urgent";

interface RoomIssue {
  hasIssue: boolean;
  description: string;
  photo: File | null;
  severity: IssueSeverity;
}

export type SubmitCleaningParams = {
  propertyId: string;
  checklistId: string | null;
  notes: string;
  issues: { room_name: string; description: string; photo_url: string | null; severity: IssueSeverity }[];
  photos: { room_name: string; photo_url: string; task_text: string | null }[];
  tasksStatus: { room_name: string; task_text: string; completed: boolean }[];
  completionPercentage: number;
  submissionId?: string;
};

interface CleaningTabProps {
  propertyId: string;
  taskId?: string;
  readOnly?: boolean;
  checklistId?: string | null;
  checklistName?: string | null;
  timerStartedAt?: string | null;
  onProgressChange?: (data: { completedCount: number; totalTasks: number; isSaving: boolean }) => void;
  onChecklistNameChange?: (name: string) => void;
  mainHeaderRef?: React.RefObject<HTMLElement>;
  onChecklistSubmitted?: () => void;
  /** When set, used instead of useSubmitCleaning (for owner/team task runner). */
  submitCleaningAsOwnerOrTeam?: { mutateAsync: (params: Omit<SubmitCleaningParams, "checklistId"> & { propertyId: string }) => Promise<unknown> };
  /** When set (e.g. owner/team runner), used for inline room inventory updates and request/purchase sheet. */
  submitInventoryAsOwnerOrTeam?: { mutateAsync: (params: { propertyId: string; items: { item_id: string; item_name: string; count: number; fullness_value?: number }[]; cleaningTaskId?: string; onlyUpdateItems?: boolean }) => Promise<unknown> };
  /** For request/purchase sheet when running as owner/team. */
  reimbursementMode?: string;
  /** Entry walkthrough JSON; used to merge proof URLs and hide required standards already captured at entry. */
  entryWalkthroughState?: Json | null;
  /** When false and taskId is set, checklist interactions are blocked until arrival check is finished. */
  entryWalkthroughCompleted?: boolean;
  /** When true, hide the inline elapsed clock in the checklist card header (parent shows TurnoverWizard timer). */
  hideInlineElapsedLabel?: boolean;
}

export interface UploadedPhoto {
  id: string;
  file?: File;
  previewUrl: string;
  persisted?: boolean;
  submissionPhotoId?: string;
  photoUrl?: string;
}

// Loop was in progress path; fixed with progressSnapshot + saveProgressImmediate ref. Set to true to re-run binary search.
const DISABLE_PROGRESS = false;
const DISABLE_TRANSLATION = false;
const DISABLE_PARENT_REPORTING = false;

/** Falls back when not under `[data-cleaner-turnover-chrome="1"]` / `[data-previsit-ui="1"]`. */
const TEAL_ACCENT = "var(--pv-teal, #0fa89a)";

function CleaningProgressRing({ pct }: { pct: number }) {
  const r = 18;
  const c = 2 * Math.PI * r;
  const clamped = Math.min(100, Math.max(0, pct));
  const offset = c - (clamped / 100) * c;
  return (
    <svg width={52} height={52} className="shrink-0 -rotate-90" aria-hidden>
      <circle cx={26} cy={26} r={r} fill="none" className="stroke-muted/45" strokeWidth={5} />
      <circle
        cx={26}
        cy={26}
        r={r}
        fill="none"
        stroke={TEAL_ACCENT}
        strokeWidth={5}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
      />
    </svg>
  );
}

const CleaningTab = ({
  propertyId,
  taskId,
  readOnly = false,
  checklistId,
  checklistName,
  timerStartedAt,
  onProgressChange,
  onChecklistNameChange,
  mainHeaderRef,
  onChecklistSubmitted,
  submitCleaningAsOwnerOrTeam,
  submitInventoryAsOwnerOrTeam,
  reimbursementMode = "auto_pay",
  entryWalkthroughState = null,
  entryWalkthroughCompleted,
  hideInlineRoomInventory = false,
  hideInlineElapsedLabel = false,
}: CleaningTabProps) => {
  const { t, tDynamic, lang, isRtl } = useTranslation();
  const { translateChecklist, isTranslating } = useDynamicChecklistTranslation();
  const { data: checklist, isLoading } = useChecklistForCleaner(checklistId);

  const dbg = (name: string) => {
    // eslint-disable-next-line no-console
    console.log(`[DBG] ${name}`, { taskId, checklistId, lang, time: Date.now() });
    // eslint-disable-next-line no-console
    console.trace();
  };
  const { data: referencePhotosData } = useReferencePhotos(propertyId, checklistId ?? undefined);
  const { data: taskReferencePhotosData } = useTaskReferencePhotos(propertyId, checklistId ?? undefined);
  const referencePhotos = referencePhotosData?.list;
  const taskReferencePhotos = taskReferencePhotosData?.list;
  const { data: guideEntries = [] } = usePropertyGuideEntries(propertyId);
  const submitCleaning = useSubmitCleaning();
  const createMaintenanceReport = useCreateMaintenanceReport();
  const { data: cleanerRecord } = useCleanerRecord();
  const { data: proofStandardsList = [] } = useProofStandardsForChecklist(checklistId ?? undefined);
  const requiredProofStandards = useMemo(
    () => proofStandardsList.filter((s) => s.required_every_turnover),
    [proofStandardsList]
  );
  const entryProofUrlsRaw = useMemo(
    () => parseProofMediaByStandardIdFromEntryState(entryWalkthroughState),
    [entryWalkthroughState]
  );
  const entryProofUrls = useMemo(
    () => mergeProofMediaByStandardForSubmission(entryProofUrlsRaw),
    [entryProofUrlsRaw]
  );
  const entryFunctionalPassIds = useMemo(
    () => parseFunctionalPassStandardIdsFromEntryState(entryWalkthroughState),
    [entryWalkthroughState]
  );
  const checklistRoomNames = useMemo(
    () => (checklist?.rooms as unknown as Room[] | undefined)?.map((r) => r.name).filter(Boolean) ?? [],
    [checklist?.rooms]
  );
  const upsertStandardProofUpload = useUpsertStandardProofUpload();

  const proofStandardsForCleaningList = useMemo(
    () =>
      proofStandardsList.filter((s) => {
        if (!s.required_every_turnover) return true;
        return !isProofStandardFullySatisfied(
          s,
          checklistRoomNames,
          entryProofUrlsRaw,
          entryFunctionalPassIds,
        );
      }),
    [proofStandardsList, entryProofUrlsRaw, entryFunctionalPassIds, checklistRoomNames]
  );
  const requiredProofStandardsStillInList = useMemo(
    () => proofStandardsForCleaningList.filter((s) => s.required_every_turnover),
    [proofStandardsForCleaningList]
  );

  // Original rooms from checklist (must be before roomItemsByInvRoomId / filteredRoomsForDisplay)
  const originalRooms = useMemo(() => (checklist?.rooms as unknown as Room[]) || [], [checklist?.rooms]);

  // Inline room inventory: shared rooms + items
  const { data: inventoryRooms = [] } = useInventoryRooms(propertyId);
  const { data: inventoryItems = [] } = useInventoryItems(propertyId);
  const submitInventoryDefault = useSubmitInventory();
  const submitInventory = submitInventoryAsOwnerOrTeam ?? submitInventoryDefault;

  const getInventoryRoomId = useCallback(
    (room: Room): string | null => {
      const propRoomId = (room as { property_room_id?: string | null }).property_room_id ?? null;
      if (propRoomId) {
        const byPropRoom = inventoryRooms.find(
          (r) => (r as { property_room_id?: string | null }).property_room_id === propRoomId
        );
        if (byPropRoom) return byPropRoom.id;
      }
      const id = room.inventory_room_id ?? null;
      if (id) return id;
      const nameKey = (room.name || "").trim().toLowerCase().replace(/\s+/g, " ");
      if (!nameKey) return null;
      const found = inventoryRooms.find(
        (r) => (r.name || "").trim().toLowerCase().replace(/\s+/g, " ") === nameKey
      );
      return found?.id ?? null;
    },
    [inventoryRooms]
  );

  const roomItemsByInvRoomId = useMemo(() => {
    const map: Record<string, RoomInventoryItem[]> = {};
    inventoryItems.forEach((item) => {
      const rid = (item as { room_id?: string | null }).room_id ?? null;
      if (!rid) return;
      if (!map[rid]) map[rid] = [];
      map[rid].push(item as RoomInventoryItem);
    });
    return map;
  }, [inventoryItems]);

  const [selectedItemForSheet, setSelectedItemForSheet] = useState<RoomInventoryItem | null>(null);
  const [inventorySheetOpen, setInventorySheetOpen] = useState(false);
  const [inventorySheetInitialMode, setInventorySheetInitialMode] = useState<"request" | "purchase" | "idle">("idle");

  const { data: turnoverPurchasesForSheet = [] } = useTurnoverPurchases(taskId);
  const requestOrderByItemIdForSheet = useMemo(() => {
    const m = new Map<string, (typeof turnoverPurchasesForSheet)[number]>();
    for (const p of turnoverPurchasesForSheet) {
      if (p.type !== "request_order") continue;
      if (p.item_id && !m.has(p.item_id)) m.set(p.item_id, p);
      if (p.line_items?.length) {
        for (const li of p.line_items) {
          if (!m.has(li.inventory_item_id)) m.set(li.inventory_item_id, p);
        }
      }
    }
    return m;
  }, [turnoverPurchasesForSheet]);
  const hasRequestOrderForItem = useCallback(
    (itemId: string) => requestOrderByItemIdForSheet.has(itemId),
    [requestOrderByItemIdForSheet]
  );

  const handleUpdateInventoryItem = useCallback(
    async (params: {
      item_id: string;
      item_name: string;
      count?: number;
      fullness_value?: number;
    }) => {
      await submitInventory.mutateAsync({
        propertyId,
        items: [
          {
            item_id: params.item_id,
            item_name: params.item_name,
            count: params.count ?? 0,
            fullness_value: params.fullness_value,
          },
        ],
        cleaningTaskId: taskId ?? undefined,
        onlyUpdateItems: true,
      });
    },
    [propertyId, taskId, submitInventory]
  );

  const openRequestSheet = useCallback((item: RoomInventoryItem) => {
    setSelectedItemForSheet(item);
    setInventorySheetInitialMode("request");
    setInventorySheetOpen(true);
  }, []);
  const openPurchaseSheet = useCallback((item: RoomInventoryItem) => {
    setSelectedItemForSheet(item);
    setInventorySheetInitialMode("purchase");
    setInventorySheetOpen(true);
  }, []);

  // Use provided ref or create internal one
  const internalHeaderRef = useRef<HTMLDivElement>(null);
  const headerRef = (mainHeaderRef as React.RefObject<HTMLDivElement>) || internalHeaderRef;
  
  // Progress persistence hook
  const { progress, isLoading: progressLoading, saveProgress, saveProgressImmediate, deleteProgress, isSaving } = useCleaningProgress(taskId);
  const { data: submissionForTask } = useLatestSubmissionForTask(taskId ?? undefined);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const completedTasksRef = useRef<Set<string>>(new Set());
  const totalTasksCountRef = useRef(0);

  // Translated checklist state
  const [translatedChecklistName, setTranslatedChecklistName] = useState<string>("");
  const [translatedRooms, setTranslatedRooms] = useState<Room[]>([]);

  const getReferencePhotoForRoom = (roomName: string) => {
    return referencePhotos?.find((p) => p.room_name === roomName);
  };

  // Get multiple reference photos for a task
  const getTaskReferencePhotos = (roomName: string, taskText: string) => {
    return getPhotosForTask(taskReferencePhotos, roomName.trim(), taskText.trim());
  };

  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [galleryRoom, setGalleryRoom] = useState<Room | null>(null);
  const [notes, setNotes] = useState("");
  const [roomPhotos, setRoomPhotos] = useState<Record<string, File | null>>({});
  
  // Multi-photo state for before/after
  const [taskBeforePhotos, setTaskBeforePhotos] = useState<Record<string, UploadedPhoto[]>>({});
  const [taskAfterPhotos, setTaskAfterPhotos] = useState<Record<string, UploadedPhoto[]>>({});
  const [draftSubmissionId, setDraftSubmissionId] = useState<string | null>(null);
  const draftSubmissionPromiseRef = useRef<Promise<string | null> | null>(null);
  const loadedPhotosSubmissionIdRef = useRef<string | null>(null);
  
  // Maintenance task state - keyed by taskKey
  const [maintenanceResponses, setMaintenanceResponses] = useState<Record<string, MaintenanceResponseValue>>({});
  const [maintenanceNotes, setMaintenanceNotes] = useState<Record<string, string>>({});
  const [maintenancePhotos, setMaintenancePhotos] = useState<Record<string, UploadedPhoto[]>>({});
  
  // Quick check task state - keyed by taskKey
  const [quickCheckVerified, setQuickCheckVerified] = useState<Record<string, boolean>>({});
  const [quickCheckPhotos, setQuickCheckPhotos] = useState<Record<string, UploadedPhoto[]>>({});

  // Block completion when reference photos are required but missing; show persistent message
  const [showReferencePhotosRequired, setShowReferencePhotosRequired] = useState(false);
  const referencePhotosAlertRef = useRef<HTMLDivElement>(null);
  
  const [roomIssues, setRoomIssues] = useState<Record<string, RoomIssue>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const [expandedRooms, setExpandedRooms] = useState<Set<number>>(() => new Set());
  const [proofByStandardId, setProofByStandardId] = useState<Record<string, string[]>>({});
  const [proofReferenceModal, setProofReferenceModal] = useState<ProofStandard | null>(null);
  const [proofUploadingStandardId, setProofUploadingStandardId] = useState<string | null>(null);
  type ChecklistViewMode = "room" | "workflow";
  const [checklistViewMode, setChecklistViewMode] = useState<ChecklistViewMode>("room");
  type CleaningMainTab = "checklist" | "issues";
  const [cleaningMainTab, setCleaningMainTab] = useState<CleaningMainTab>("checklist");
  const [workflowExplainerOpen, setWorkflowExplainerOpen] = useState(false);
  const [preCleaningViewIntroOpen, setPreCleaningViewIntroOpen] = useState(false);
  const [workflowFilter, setWorkflowFilter] = useState<CleaningCategory | "all">("all");
  /** Room view: filter tasks by cleaning category; "all" = no filter. */
  const [cleaningCategoryFilter, setCleaningCategoryFilter] = useState<CleaningCategory | "all">("all");

  const [cleaningElapsedLabel, setCleaningElapsedLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!timerStartedAt) {
      setCleaningElapsedLabel(null);
      return;
    }
    const tick = () => {
      const start = new Date(timerStartedAt).getTime();
      const now = Date.now();
      const diff = Math.floor((now - start) / 1000);
      const hours = Math.floor(diff / 3600);
      const mins = Math.floor((diff % 3600) / 60);
      const secs = diff % 60;
      if (hours > 0) {
        setCleaningElapsedLabel(
          `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
        );
      } else {
        setCleaningElapsedLabel(`${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timerStartedAt]);

  const prevProofTaskIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!taskId) {
      setProofByStandardId({});
      prevProofTaskIdRef.current = undefined;
      return;
    }
    if (prevProofTaskIdRef.current !== taskId) {
      prevProofTaskIdRef.current = taskId;
      setProofByStandardId({ ...entryProofUrls });
      return;
    }
    setProofByStandardId((prev) => ({ ...entryProofUrls, ...prev }));
  }, [taskId, entryProofUrls]);

  const originalRoomsRef = useRef<Room[]>([]);
  useEffect(() => {
    originalRoomsRef.current = originalRooms;
  }, [originalRooms]);

  // Display values: English is derived (no state); non-English use translated state when available.
  const displayChecklistName =
    lang === "en" ? (checklist?.name || "") : (translatedChecklistName || checklist?.name || "");

  const rooms =
    lang === "en" ? originalRooms : (translatedRooms.length > 0 ? translatedRooms : originalRooms);

  const checklistHeaderStats = useMemo(() => {
    const total = rooms.reduce((sum, room) => sum + (room.tasks?.length ?? 0), 0);
    const completed = completedTasks.size;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, pct };
  }, [rooms, completedTasks]);
  const prevTaskIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (DISABLE_PROGRESS) return;
    dbg("effect: taskId changed → setProgressLoaded(false)");
    if (prevTaskIdRef.current !== taskId) {
      prevTaskIdRef.current = taskId;
      setProgressLoaded(false);
    }
  }, [taskId]);

  // Load saved progress when available. Use primitive snapshot so effect does NOT re-run on every progress object identity change (React Query can return new ref each time → loop).
  const progressAppliedRef = useRef<string | null>(null);
  const progressSnapshot =
    progressLoading ? `${taskId}-loading` : (progress ? `${taskId}-${(progress.completed_tasks?.length ?? 0)}` : `${taskId}-null`);
  useEffect(() => {
    if (DISABLE_PROGRESS) return;
    dbg("effect: progress load → setCompletedTasks/setProgressLoaded");
    if (progressLoading) return;
    if (progress && !progressLoaded) {
      const appliedKey = `${taskId}-${progress.completed_tasks?.length ?? 0}`;
      if (progressAppliedRef.current === appliedKey) return;
      progressAppliedRef.current = appliedKey;
      const savedTasks = progress.completed_tasks || [];
      if (savedTasks.length > 0 && !sameSet(completedTasksRef.current, savedTasks)) {
        setCompletedTasks(new Set(savedTasks));
      }
      setProgressLoaded(true);
      return;
    }
    if (!progress && !progressLoaded) {
      const tasksStatus = submissionForTask?.tasks_status as { room_name: string; task_text: string; completed: boolean }[] | undefined;
      if (tasksStatus && Array.isArray(tasksStatus) && tasksStatus.length > 0) {
        const appliedKey = `submission-${taskId}-${tasksStatus.length}`;
        if (progressAppliedRef.current === appliedKey) return;
        progressAppliedRef.current = appliedKey;
        const completedKeys = tasksStatus.filter((t) => t.completed).map((t) => `${t.room_name}::${t.task_text}`);
        if (completedKeys.length > 0 && !sameSet(completedTasksRef.current, completedKeys)) {
          setCompletedTasks(new Set(completedKeys));
        }
        setProgressLoaded(true);
        return;
      }
      if (progressAppliedRef.current === "empty") return;
      progressAppliedRef.current = "empty";
      setProgressLoaded(true);
    }
  // progressSnapshot is primitive so effect doesn't re-run on progress object identity change; progress/taskId read from closure
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: progress/taskId encoded in progressSnapshot, submissionForTask for owner/team restore
  }, [progressSnapshot, progressLoaded, progressLoading, submissionForTask]);

  // Translation: English never sets state (derived above). Non-English async with stale guard.
  const translateReqRef = useRef(0);

  useEffect(() => {
    if (DISABLE_TRANSLATION) return;
    dbg("effect: translation → setTranslatedChecklistName/setTranslatedRooms");
    // If no checklist, clear only if we previously had translations
    if (!checklist || originalRooms.length === 0) {
      translateReqRef.current += 1; // invalidate in-flight
      if (translatedChecklistName !== "" || translatedRooms.length > 0) {
        setTranslatedChecklistName("");
        setTranslatedRooms([]);
      }
      return;
    }

    // English: do NOT set translated state at all
    if (lang === "en") {
      translateReqRef.current += 1; // invalidate in-flight
      if (translatedChecklistName !== "" || translatedRooms.length > 0) {
        setTranslatedChecklistName("");
        setTranslatedRooms([]);
      }
      return;
    }

    // Non-English: async translate with stale guard
    const reqId = ++translateReqRef.current;

    translateChecklist(checklist.name, originalRooms, lang).then((result) => {
      if (translateReqRef.current !== reqId) return; // stale
      setTranslatedChecklistName(result.name);
      setTranslatedRooms(result.rooms);
    });
  }, [checklist?.id, checklist?.name, lang, originalRooms, translateChecklist]);

  // Keep ref in sync for flush-on-unmount
  useEffect(() => {
    completedTasksRef.current = completedTasks;
  }, [completedTasks]);

  useEffect(() => {
    totalTasksCountRef.current = originalRooms.reduce((sum, room) => sum + (room.tasks?.length ?? 0), 0);
  }, [originalRooms]);

  // Unmount save: use ref so effect only runs when taskId/readOnly change, not when saveProgressImmediate identity changes (avoids cleanup → save → rerender → new callback → effect re-runs → loop).
  const saveProgressImmediateRef = useRef(saveProgressImmediate);
  saveProgressImmediateRef.current = saveProgressImmediate;

  useEffect(() => {
    dbg("effect: unmount-save registered");
    return () => {
      if (DISABLE_PROGRESS) return;
      dbg("effect: unmount-save cleanup (saveProgressImmediate)");
      if (readOnly || !taskId) return;
      const latest = completedTasksRef.current;
      if (latest.size === 0) return;

      const rooms = originalRoomsRef.current;
      const totalTasksCount = rooms.reduce((sum, room) => sum + (room.tasks?.length ?? 0), 0);
      const progressPercent = totalTasksCount > 0 ? Math.round((latest.size / totalTasksCount) * 100) : 0;

      saveProgressImmediateRef.current(Array.from(latest), progressPercent);
    };
  }, [readOnly, taskId]);

  // Refs for parent callbacks — effect must not depend on callback identity (parent often passes non-memoized).
  const onProgressChangeRef = useRef(onProgressChange);
  const onChecklistNameChangeRef = useRef(onChecklistNameChange);
  useEffect(() => {
    onProgressChangeRef.current = onProgressChange;
  }, [onProgressChange]);
  useEffect(() => {
    onChecklistNameChangeRef.current = onChecklistNameChange;
  }, [onChecklistNameChange]);

  const totalTasksCount = useMemo(() => {
    return originalRooms.reduce((sum, room) => sum + (room.tasks?.length ?? 0), 0);
  }, [originalRooms]);

  const lastProgressRef = useRef({ completedCount: -1, totalTasks: -1, isSaving: false });

  useEffect(() => {
    if (DISABLE_PARENT_REPORTING) return;
    dbg("effect: report progress to parent");
    const cb = onProgressChangeRef.current;
    if (!cb) return;

    const completedCount = completedTasks.size;
    const prev = lastProgressRef.current;

    if (
      prev.completedCount === completedCount &&
      prev.totalTasks === totalTasksCount &&
      prev.isSaving === isSaving
    ) return;

    lastProgressRef.current = { completedCount, totalTasks: totalTasksCount, isSaving };

    cb({ completedCount, totalTasks: totalTasksCount, isSaving });
  }, [completedTasks.size, totalTasksCount, isSaving]);

  const lastChecklistNameRef = useRef<string>("");

  useEffect(() => {
    if (DISABLE_PARENT_REPORTING) return;
    dbg("effect: report checklist name to parent");
    const cb = onChecklistNameChangeRef.current;
    if (!cb) return;

    if (!displayChecklistName) return;
    if (lastChecklistNameRef.current === displayChecklistName) return;

    lastChecklistNameRef.current = displayChecklistName;
    cb(displayChecklistName);
  }, [displayChecklistName]);

  const getTaskKey = (roomName: string, task: string) => `${roomName}::${task}`;

  // Workflow view: only cleaning tasks grouped by cleaning category
  const workflowGroups = useMemo(() => {
    const groups: Record<string, { roomName: string; roomIndex: number; task: Task; taskIndex: number }[]> = {};
    (originalRooms || []).forEach((room, roomIndex) => {
      (room.tasks || []).forEach((task, taskIndex) => {
        if (!isCleaningTask(task)) return;
        const text = getTaskText(task);
        if (!text?.trim()) return;
        const cat = getTaskCleaningCategory(task);
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push({ roomName: room.name, roomIndex, task, taskIndex });
      });
    });
    return groups;
  }, [originalRooms]);

  /** Beds & linens tasks appear under Laundry in workflow view. */
  const workflowGroupsDisplay = useMemo(() => {
    const g = { ...workflowGroups };
    const merged = [...(g.laundry || []), ...(g.beds_linens || [])];
    if (merged.length) g.laundry = merged;
    delete g.beds_linens;
    return g;
  }, [workflowGroups]);

  const workflowFilteredEntries = useMemo(() => {
    if (workflowFilter === "all") {
      return WORKFLOW_VIEW_CATEGORY_KEYS.flatMap((c) =>
        (workflowGroupsDisplay[c] || []).map((entry) => ({ ...entry, subcategory: c }))
      );
    }
    if (workflowFilter === "guest_amenities") return [];
    return (workflowGroupsDisplay[workflowFilter] || []).map((entry) => ({ ...entry, subcategory: workflowFilter }));
  }, [workflowFilter, workflowGroupsDisplay]);

  useEffect(() => {
    if (checklistViewMode !== "workflow") return;
    if (workflowFilter === "guest_amenities") setWorkflowFilter("all");
  }, [checklistViewMode, workflowFilter]);

  // Room view: when cleaning category filter is set, show only rooms that have at least one matching task;
  // within each room show matching cleaning tasks + all maintenance + quickcheck.
  // Derive roomList from originalRooms/translatedRooms inside useMemo to keep hook order stable (avoids React #310).
  const filteredRoomsForDisplay = useMemo(() => {
    const roomList: Room[] =
      lang === "en" ? originalRooms : (translatedRooms.length > 0 ? translatedRooms : originalRooms);
    if (cleaningCategoryFilter === "all") {
      return roomList.map((room, roomIndex) => ({ roomIndex, room, filteredRoom: room }));
    }
    return roomList
      .map((room, roomIndex) => {
        const filteredTasks = (room.tasks || []).filter((task) => {
          if (!isCleaningTask(task)) return true; // always show maintenance & quickcheck
          return getTaskCleaningCategory(task) === cleaningCategoryFilter;
        });
        if (filteredTasks.length === 0) return null;
        return { roomIndex, room, filteredRoom: { ...room, tasks: filteredTasks } as Room };
      })
      .filter((e): e is { roomIndex: number; room: Room; filteredRoom: Room } => e != null);
  }, [lang, originalRooms, translatedRooms, cleaningCategoryFilter]);

  // Room view: keep every room expanded by default so cleaners see all tasks without tapping headers.
  useLayoutEffect(() => {
    if (checklistViewMode !== "room") return;
    const n = originalRooms.length;
    if (n === 0) return;
    setExpandedRooms(new Set(Array.from({ length: n }, (_, i) => i)));
  }, [checklistViewMode, checklist?.id, originalRooms.length]);

  const nextFocusTaskKey = useMemo(() => {
    if (checklistViewMode === "workflow") {
      for (const entry of workflowFilteredEntries) {
        const originalRoom = originalRooms[entry.roomIndex];
        const originalRoomName = originalRoom?.name ?? entry.roomName;
        const origTask = originalRoom?.tasks?.[entry.taskIndex];
        const origTaskObj = origTask ? normalizeTask(origTask as Task) : normalizeTask(entry.task);
        const text = origTaskObj.text;
        if (!completedTasks.has(getTaskKey(originalRoomName, text))) {
          return getTaskKey(originalRoomName, text);
        }
      }
      return null;
    }
    for (const { roomIndex, room, filteredRoom } of filteredRoomsForDisplay) {
      const originalRoom = originalRooms[roomIndex];
      const originalRoomName = originalRoom?.name ?? room.name;
      const tasks = filteredRoom.tasks || [];
      for (let taskIndex = 0; taskIndex < tasks.length; taskIndex++) {
        const task = tasks[taskIndex];
        const origTask = originalRoom?.tasks?.[taskIndex];
        const origTaskObj = origTask ? normalizeTask(origTask as Task) : normalizeTask(task);
        if (getTaskType(origTaskObj) !== "cleaning") continue;
        const text = origTaskObj.text;
        if (!completedTasks.has(getTaskKey(originalRoomName, text))) {
          return getTaskKey(originalRoomName, text);
        }
      }
    }
    return null;
  }, [
    checklistViewMode,
    workflowFilteredEntries,
    filteredRoomsForDisplay,
    originalRooms,
    completedTasks,
  ]);


  const issuesTabCount = useMemo(
    () => Object.values(roomIssues).filter((i) => i?.hasIssue).length,
    [roomIssues]
  );

  // Calculate progress percentage
  const calculateProgressPercent = useCallback((tasks: Set<string>) => {
    const totalTasks = originalRooms.reduce((sum, room) => sum + (room.tasks?.length ?? 0), 0);
    return totalTasks > 0 ? Math.round((tasks.size / totalTasks) * 100) : 0;
  }, [originalRooms]);

  const toggleTask = (roomName: string, task: string) => {
    // GUARD: Prevent editing completed tasks - if readOnly, block all interactions
    if (readOnly) {
      console.warn("[CleaningTab] Task toggle blocked - task is read-only (completed)");
      return;
    }
    const key = getTaskKey(roomName, task);
    const newSet = new Set(completedTasks);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setCompletedTasks(newSet);
    
    // Save progress (debounced)
    if (taskId && !readOnly) {
      const completedArray = Array.from(newSet);
      const progressPercent = calculateProgressPercent(newSet);
      saveProgress(completedArray, progressPercent);
    }
  };

  const isTaskCompleted = (roomName: string, task: string) => {
    return completedTasks.has(getTaskKey(roomName, task));
  };

  const nextWorkflowCategory = useMemo(() => {
    if (checklistViewMode !== "workflow" || workflowFilter === "all") return null;
    const idx = WORKFLOW_VIEW_CATEGORY_KEYS.indexOf(workflowFilter as CleaningCategory);
    if (idx < 0) return null;
    for (let j = idx + 1; j < WORKFLOW_VIEW_CATEGORY_KEYS.length; j++) {
      const cat = WORKFLOW_VIEW_CATEGORY_KEYS[j];
      const group = workflowGroupsDisplay[cat];
      if (!group?.length) continue;
      const anyIncomplete = group.some(
        (e) => !completedTasks.has(getTaskKey(e.roomName, getTaskText(e.task)))
      );
      if (anyIncomplete) return cat;
    }
    return null;
  }, [checklistViewMode, workflowFilter, workflowGroupsDisplay, completedTasks]);

  useEffect(() => {
    if (!timerStartedAt || !taskId || readOnly || !checklist) return;
    const k = `cleaning_view_intro_v2_${taskId}`;
    try {
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(k)) return;
      setPreCleaningViewIntroOpen(true);
      sessionStorage.setItem(k, "1");
    } catch {
      setPreCleaningViewIntroOpen(true);
    }
  }, [timerStartedAt, taskId, readOnly, checklist?.id]);

  const entryGateActive =
    Boolean(taskId) &&
    !readOnly &&
    (!entryWalkthroughCompleted || !timerStartedAt);

  const handlePhotoSelect = (roomName: string, file: File | null) => {
    setRoomPhotos((prev) => ({ ...prev, [roomName]: file }));
  };

  useEffect(() => {
    if (submissionForTask?.id) {
      setDraftSubmissionId((prev) => prev ?? submissionForTask.id);
    }
  }, [submissionForTask?.id]);

  useEffect(() => {
    const loadPersistedTaskPhotos = async () => {
      if (!submissionForTask?.id) return;
      if (loadedPhotosSubmissionIdRef.current === submissionForTask.id) return;
      loadedPhotosSubmissionIdRef.current = submissionForTask.id;

      const { data, error } = await supabase
        .from("task_submission_photos")
        .select("id, room_name, task_text, photo_url, type")
        .eq("submission_id", submissionForTask.id)
        .order("display_order", { ascending: true });

      if (error) {
        console.error("[CleaningTab] Failed to load persisted task photos:", error);
        return;
      }

      const beforeMap: Record<string, UploadedPhoto[]> = {};
      const afterMap: Record<string, UploadedPhoto[]> = {};
      for (const row of data ?? []) {
        const key = getTaskKey(row.room_name, row.task_text);
        const photo: UploadedPhoto = {
          id: `persisted-${row.id}`,
          previewUrl: row.photo_url,
          persisted: true,
          submissionPhotoId: row.id,
          photoUrl: row.photo_url,
        };
        if (row.type === "before") {
          beforeMap[key] = [...(beforeMap[key] || []), photo];
        } else {
          afterMap[key] = [...(afterMap[key] || []), photo];
        }
      }

      setTaskBeforePhotos((prev) => ({ ...beforeMap, ...prev }));
      setTaskAfterPhotos((prev) => ({ ...afterMap, ...prev }));
    };

    void loadPersistedTaskPhotos();
  }, [submissionForTask?.id]);

  const ensureDraftSubmissionId = useCallback(async (): Promise<string | null> => {
    if (draftSubmissionId) return draftSubmissionId;
    if (draftSubmissionPromiseRef.current) return draftSubmissionPromiseRef.current;
    if (!taskId || !cleanerRecord?.id) return null;

    draftSubmissionPromiseRef.current = (async () => {
      const id = generateUUID();
      const { error } = await supabase.from("cleaning_submissions").insert({
        id,
        property_id: propertyId,
        cleaner_id: cleanerRecord.id,
        cleaning_task_id: taskId,
        checklist_id: checklistId ?? null,
        notes: "",
        issues: [],
        photos: [],
        tasks_status: [],
        completion_percentage: 0,
      });

      if (error) {
        console.error("[CleaningTab] Failed to create draft submission for autosave:", error);
        return null;
      }

      setDraftSubmissionId(id);
      return id;
    })();

    const created = await draftSubmissionPromiseRef.current;
    draftSubmissionPromiseRef.current = null;
    return created;
  }, [draftSubmissionId, taskId, checklistId, cleanerRecord?.id, propertyId]);

  const uploadTaskPhotoImmediately = useCallback(
    async (roomName: string, taskText: string, photoType: "before" | "after", photo: UploadedPhoto, displayOrder: number) => {
      if (!photo.file) return;
      const submissionId = await ensureDraftSubmissionId();
      if (!submissionId) return;

      const fileExt = photo.file.name.split(".").pop()?.toLowerCase() || "jpg";
      const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "_");
      const sanitizedTask = taskText.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30);
      const timestamp = Date.now();
      const fileName = `cleaning_submissions/${submissionId}/${sanitizedRoomName}/${sanitizedTask}_${photoType}_${timestamp}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("cleaning-photos")
        .upload(fileName, photo.file, {
          contentType: photo.file.type || `image/${fileExt === "jpg" ? "jpeg" : fileExt}`,
          upsert: true,
        });

      if (uploadError) {
        console.error("[CleaningTab] Immediate task photo upload failed:", uploadError);
        toast({ title: t("error"), description: tDynamic("Photo upload failed. Please try again."), variant: "destructive" });
        return;
      }

      const { data: urlData } = supabase.storage.from("cleaning-photos").getPublicUrl(fileName);
      const { data: inserted, error: insertError } = await supabase
        .from("task_submission_photos")
        .insert({
          submission_id: submissionId,
          room_name: roomName,
          task_text: taskText,
          photo_url: urlData.publicUrl,
          type: photoType,
          display_order: displayOrder,
        })
        .select("id, photo_url")
        .single();

      if (insertError) {
        console.error("[CleaningTab] Immediate task photo DB insert failed:", insertError);
        toast({ title: t("error"), description: tDynamic("Photo save failed. Please try again."), variant: "destructive" });
        return;
      }

      const patchPhoto = (list: UploadedPhoto[]) =>
        list.map((p) =>
          p.id === photo.id
            ? {
                ...p,
                persisted: true,
                submissionPhotoId: inserted.id,
                photoUrl: inserted.photo_url,
              }
            : p
        );

      if (photoType === "before") {
        const key = getTaskKey(roomName, taskText);
        setTaskBeforePhotos((prev) => ({ ...prev, [key]: patchPhoto(prev[key] || []) }));
      } else {
        const key = getTaskKey(roomName, taskText);
        setTaskAfterPhotos((prev) => ({ ...prev, [key]: patchPhoto(prev[key] || []) }));
      }
    },
    [ensureDraftSubmissionId, getTaskKey, t, tDynamic]
  );

  // Multi-photo handlers
  const handleAddBeforePhotos = (roomName: string, taskText: string, files: File[]) => {
    const key = getTaskKey(roomName, taskText);
    const newPhotos: UploadedPhoto[] = files.map(file => ({
      id: generateUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    const startOrder = (taskBeforePhotos[key] || []).length;
    setTaskBeforePhotos(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), ...newPhotos],
    }));
    newPhotos.forEach((photo, idx) => {
      void uploadTaskPhotoImmediately(roomName, taskText, "before", photo, startOrder + idx);
    });
  };

  const handleRemoveBeforePhoto = (roomName: string, taskText: string, photoId: string) => {
    const key = getTaskKey(roomName, taskText);
    setTaskBeforePhotos(prev => {
      const current = prev[key] || [];
      const photoToRemove = current.find(p => p.id === photoId);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
        if (photoToRemove.submissionPhotoId) {
          void supabase.from("task_submission_photos").delete().eq("id", photoToRemove.submissionPhotoId);
        }
      }
      return {
        ...prev,
        [key]: current.filter(p => p.id !== photoId),
      };
    });
  };

  const handleAddAfterPhotos = (roomName: string, taskText: string, files: File[]) => {
    const key = getTaskKey(roomName, taskText);
    const newPhotos: UploadedPhoto[] = files.map(file => ({
      id: generateUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    const startOrder = (taskAfterPhotos[key] || []).length;
    setTaskAfterPhotos(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), ...newPhotos],
    }));
    newPhotos.forEach((photo, idx) => {
      void uploadTaskPhotoImmediately(roomName, taskText, "after", photo, startOrder + idx);
    });
  };

  const handleRemoveAfterPhoto = (roomName: string, taskText: string, photoId: string) => {
    const key = getTaskKey(roomName, taskText);
    setTaskAfterPhotos(prev => {
      const current = prev[key] || [];
      const photoToRemove = current.find(p => p.id === photoId);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
        if (photoToRemove.submissionPhotoId) {
          void supabase.from("task_submission_photos").delete().eq("id", photoToRemove.submissionPhotoId);
        }
      }
      return {
        ...prev,
        [key]: current.filter(p => p.id !== photoId),
      };
    });
  };

  const toggleRoomIssue = useCallback((roomName: string, hasIssue: boolean) => {
    setRoomIssues((prev) => ({
      ...prev,
      [roomName]: {
        hasIssue,
        description: prev[roomName]?.description || "",
        photo: prev[roomName]?.photo || null,
        severity: prev[roomName]?.severity ?? "medium",
      },
    }));
  }, []);

  const updateRoomIssueDescription = useCallback((roomName: string, description: string) => {
    setRoomIssues((prev) => ({
      ...prev,
      [roomName]: {
        ...(prev[roomName] ?? { hasIssue: true, description: "", photo: null, severity: "medium" }),
        description,
      },
    }));
  }, []);

  const updateRoomIssuePhoto = useCallback((roomName: string, photo: File | null) => {
    setRoomIssues((prev) => ({
      ...prev,
      [roomName]: {
        ...(prev[roomName] ?? { hasIssue: true, description: "", photo: null, severity: "medium" }),
        photo,
      },
    }));
  }, []);

  const updateRoomIssueSeverity = useCallback((roomName: string, severity: IssueSeverity) => {
    setRoomIssues((prev) => ({
      ...prev,
      [roomName]: {
        ...(prev[roomName] ?? { hasIssue: true, description: "", photo: null, severity: "medium" }),
        severity,
      },
    }));
  }, []);

  // Maintenance task handlers
  const handleMaintenanceResponseChange = (taskKey: string, response: MaintenanceResponseValue) => {
    setMaintenanceResponses(prev => ({ ...prev, [taskKey]: response }));
    // Mark task as completed when response is given
    if (response !== null) {
      setCompletedTasks(prev => {
        const next = new Set(prev);
        next.add(taskKey);
        return next;
      });
    }
  };

  const handleMaintenanceNotesChange = (taskKey: string, noteText: string) => {
    setMaintenanceNotes(prev => ({ ...prev, [taskKey]: noteText }));
  };

  const handleMaintenanceAddPhotos = (taskKey: string, files: File[]) => {
    const newPhotos: UploadedPhoto[] = files.map(file => ({
      id: generateUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setMaintenancePhotos(prev => ({
      ...prev,
      [taskKey]: [...(prev[taskKey] || []), ...newPhotos],
    }));
  };

  const handleMaintenanceRemovePhoto = (taskKey: string, photoId: string) => {
    setMaintenancePhotos(prev => {
      const current = prev[taskKey] || [];
      const photoToRemove = current.find(p => p.id === photoId);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
      }
      return {
        ...prev,
        [taskKey]: current.filter(p => p.id !== photoId),
      };
    });
  };

  // Quick check task handlers
  const handleQuickCheckVerify = (taskKey: string) => {
    setQuickCheckVerified(prev => ({ ...prev, [taskKey]: true }));
    // Mark task as completed
    setCompletedTasks(prev => {
      const next = new Set(prev);
      next.add(taskKey);
      return next;
    });
    
    // Save progress
    if (taskId && !readOnly) {
      const newCompleted = new Set(completedTasks);
      newCompleted.add(taskKey);
      const completedArray = Array.from(newCompleted);
      const progressPercent = calculateProgressPercent(newCompleted);
      saveProgress(completedArray, progressPercent);
    }
  };

  const handleQuickCheckAddPhotos = (taskKey: string, files: File[]) => {
    const newPhotos: UploadedPhoto[] = files.map(file => ({
      id: generateUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setQuickCheckPhotos(prev => ({
      ...prev,
      [taskKey]: [...(prev[taskKey] || []), ...newPhotos],
    }));
  };

  const handleQuickCheckRemovePhoto = (taskKey: string, photoId: string) => {
    setQuickCheckPhotos(prev => {
      const current = prev[taskKey] || [];
      const photoToRemove = current.find(p => p.id === photoId);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
      }
      return {
        ...prev,
        [taskKey]: current.filter(p => p.id !== photoId),
      };
    });
  };


  const uploadMaintenanceReports = async (): Promise<void> => {
    if (!cleanerRecord?.id) return;

    for (const room of originalRooms) {
      for (const task of room.tasks) {
        const taskObj = normalizeTask(task);
        if (getTaskType(taskObj) !== 'maintenance') continue;
        
        const taskKey = getTaskKey(room.name, taskObj.text);
        const response = maintenanceResponses[taskKey];
        
        if (!response) continue; // Skip if not responded
        
        // Upload photos first
        const photoUrls: string[] = [];
        const photos = maintenancePhotos[taskKey] || [];
        
        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          const timestamp = Date.now();
          const sanitizedRoom = room.name.replace(/[^a-zA-Z0-9]/g, '_');
          const sanitizedTask = taskObj.text.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_');
          const fileName = `${propertyId}/${taskId}/maintenance/${sanitizedRoom}/${sanitizedTask}_${timestamp}_${i}.jpg`;
          
          const { data, error } = await supabase.storage
            .from("cleaning-photos")
            .upload(fileName, photo.file, {
              contentType: photo.file.type,
              upsert: true,
            });
          
          if (!error && data) {
            const { data: publicUrl } = supabase.storage
              .from("cleaning-photos")
              .getPublicUrl(data.path);
            photoUrls.push(publicUrl.publicUrl);
          }
        }
        
        // Create maintenance report
        await createMaintenanceReport.mutateAsync({
          property_id: propertyId,
          cleaning_task_id: taskId,
          task_description: taskObj.text,
          room_name: room.name,
          checklist_name: displayChecklistName,
          cleaner_id: cleanerRecord.id,
          response: response,
          notes: maintenanceNotes[taskKey] || undefined,
          photos: photoUrls,
        });
      }
    }
  };

  const uploadPhotosToTable = async (submissionId: string): Promise<void> => {
    // Upload room-level photos (still goes to cleaning_submissions.photos JSON for backward compat)
    const roomPhotoUrls: { room_name: string; photo_url: string; task_text: string | null }[] = [];
    
    for (const [roomName, file] of Object.entries(roomPhotos)) {
      if (!file) continue;
      const fileExt = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `cleaning_submissions/${submissionId}/${sanitizedRoomName}.${fileExt}`;

      // Upload with upsert and contentType to handle storage RLS and file type requirements
      const { error: uploadError } = await supabase.storage
        .from("cleaning-photos")
        .upload(fileName, file, {
          contentType: file.type || `image/${fileExt === "jpg" ? "jpeg" : fileExt}`,
          upsert: true, // Allow overwriting if file already exists
        });

      if (uploadError) {
        console.error(`[CleaningTab] Room photo upload error for ${roomName}:`, uploadError);
        throw new Error(`Failed to upload photo for ${roomName}: ${uploadError.message}`);
      }

      const { data: urlData } = supabase.storage
        .from("cleaning-photos")
        .getPublicUrl(fileName);

      roomPhotoUrls.push({ room_name: roomName, photo_url: urlData.publicUrl, task_text: null });
    }

    // Upload task-level BEFORE photos to storage and insert into task_submission_photos
    for (const [taskKey, photos] of Object.entries(taskBeforePhotos)) {
      if (!photos || photos.length === 0) continue;
      const [roomName, ...taskParts] = taskKey.split("::");
      const taskText = taskParts.join("::");

      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        if (photo.persisted || !photo.file) continue;
        const fileExt = photo.file.name.split(".").pop()?.toLowerCase() || "jpg";
        const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "_");
        const sanitizedTask = taskText.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30);
        const timestamp = Date.now() + i;
        const fileName = `cleaning_submissions/${submissionId}/${sanitizedRoomName}/${sanitizedTask}_before_${timestamp}.${fileExt}`;

        // Upload with upsert and contentType to handle storage RLS and file type requirements
        const { error: uploadError } = await supabase.storage
          .from("cleaning-photos")
          .upload(fileName, photo.file, {
            contentType: photo.file.type || `image/${fileExt === "jpg" ? "jpeg" : fileExt}`,
            upsert: true, // Allow overwriting if file already exists
          });

        if (uploadError) {
          console.error(`[CleaningTab] Task before photo upload error for ${roomName}/${taskText}:`, uploadError);
          // Continue with other photos instead of failing entire submission
          continue;
        }

        const { data: urlData } = supabase.storage
          .from("cleaning-photos")
          .getPublicUrl(fileName);

        // Insert into task_submission_photos table (required for before/after to show on report)
        const { error: insertError } = await supabase.from("task_submission_photos").insert({
          submission_id: submissionId,
          room_name: roomName,
          task_text: taskText,
          photo_url: urlData.publicUrl,
          type: "before",
          display_order: i,
        });
        if (insertError) {
          console.error(`[CleaningTab] Task before photo insert error for ${roomName}/${taskText}:`, insertError);
          throw new Error(`Failed to save before photo: ${insertError.message}`);
        }
      }
    }

    // Upload task-level AFTER photos to storage and insert into task_submission_photos
    for (const [taskKey, photos] of Object.entries(taskAfterPhotos)) {
      if (!photos || photos.length === 0) continue;
      const [roomName, ...taskParts] = taskKey.split("::");
      const taskText = taskParts.join("::");

      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        if (photo.persisted || !photo.file) continue;
        const fileExt = photo.file.name.split(".").pop()?.toLowerCase() || "jpg";
        const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "_");
        const sanitizedTask = taskText.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30);
        const timestamp = Date.now() + i;
        const fileName = `cleaning_submissions/${submissionId}/${sanitizedRoomName}/${sanitizedTask}_after_${timestamp}.${fileExt}`;

        // Upload with upsert and contentType to handle storage RLS and file type requirements
        const { error: uploadError } = await supabase.storage
          .from("cleaning-photos")
          .upload(fileName, photo.file, {
            contentType: photo.file.type || `image/${fileExt === "jpg" ? "jpeg" : fileExt}`,
            upsert: true, // Allow overwriting if file already exists
          });

        if (uploadError) {
          console.error(`[CleaningTab] Task after photo upload error for ${roomName}/${taskText}:`, uploadError);
          // Continue with other photos instead of failing entire submission
          continue;
        }

        const { data: urlData } = supabase.storage
          .from("cleaning-photos")
          .getPublicUrl(fileName);

        // Insert into task_submission_photos table (required for before/after to show on report)
        const { error: insertError } = await supabase.from("task_submission_photos").insert({
          submission_id: submissionId,
          room_name: roomName,
          task_text: taskText,
          photo_url: urlData.publicUrl,
          type: "after",
          display_order: i,
        });
        if (insertError) {
          console.error(`[CleaningTab] Task after photo insert error for ${roomName}/${taskText}:`, insertError);
          throw new Error(`Failed to save after photo: ${insertError.message}`);
        }
      }
    }

    return;
  };

  // Legacy photo array for backward compatibility with cleaning_submissions.photos
  const buildLegacyPhotosArray = async (submissionId: string): Promise<{ room_name: string; photo_url: string; task_text: string | null; photo_type?: string }[]> => {
    const uploadedUrls: { room_name: string; photo_url: string; task_text: string | null; photo_type?: string }[] = [];

    // Room-level photos
    for (const [roomName, file] of Object.entries(roomPhotos)) {
      if (!file) continue;
      const fileExt = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `cleaning_submissions/${submissionId}/${sanitizedRoomName}.${fileExt}`;

      // Upload with upsert and contentType to handle storage RLS and file type requirements
      const { error: uploadError } = await supabase.storage
        .from("cleaning-photos")
        .upload(fileName, file, {
          contentType: file.type || `image/${fileExt === "jpg" ? "jpeg" : fileExt}`,
          upsert: true, // Allow overwriting if file already exists
        });

      if (uploadError) {
        console.error(`[CleaningTab] Legacy photo upload error for ${roomName}:`, uploadError);
        throw new Error(`Failed to upload photo for ${roomName}: ${uploadError.message}`);
      }

      const { data: urlData } = supabase.storage
        .from("cleaning-photos")
        .getPublicUrl(fileName);

      uploadedUrls.push({ room_name: roomName, photo_url: urlData.publicUrl, task_text: null });
    }

    return uploadedUrls;
  };

  const uploadIssuePhotos = async (
    submissionId: string
  ): Promise<{ room_name: string; description: string; photo_url: string | null; severity: IssueSeverity }[]> => {
    const issues: { room_name: string; description: string; photo_url: string | null; severity: IssueSeverity }[] = [];

    for (const [roomName, issue] of Object.entries(roomIssues)) {
      if (!issue.hasIssue || !issue.description.trim()) continue;

      let photoUrl: string | null = null;

      if (issue.photo) {
        const fileExt = issue.photo.name.split(".").pop()?.toLowerCase() || "jpg";
        const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "_");
        const fileName = `cleaning_submissions/${submissionId}/issues/${sanitizedRoomName}.${fileExt}`;

        // Upload with upsert and contentType to handle storage RLS and file type requirements
        const { error: uploadError } = await supabase.storage
          .from("cleaning-photos")
          .upload(fileName, issue.photo, {
            contentType: issue.photo.type || `image/${fileExt === "jpg" ? "jpeg" : fileExt}`,
            upsert: true, // Allow overwriting if file already exists
          });

        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from("cleaning-photos")
            .getPublicUrl(fileName);
          photoUrl = urlData.publicUrl;
        } else {
          console.error(`[CleaningTab] Issue photo upload error for ${roomName}:`, uploadError);
          // Continue without photo - issue can still be reported without photo
        }
      }

      issues.push({
        room_name: roomName,
        description: issue.description.trim(),
        photo_url: photoUrl,
        severity: issue.severity ?? "medium",
      });
    }

    return issues;
  };

  // Validate required photos before submission
  const validateRequiredPhotos = (): { valid: boolean; message: string } => {
    const missingPhotos: string[] = [];
    
    for (const room of originalRooms) {
      for (const task of room.tasks) {
        const taskObj = normalizeTask(task);
        const taskKey = getTaskKey(room.name, taskObj.text);
        
        if (taskObj.require_before_photo && (!taskBeforePhotos[taskKey] || taskBeforePhotos[taskKey].length === 0)) {
          missingPhotos.push(`"${room.name} - ${taskObj.text}" requires a BEFORE photo`);
        }
        if (taskObj.require_after_photo && (!taskAfterPhotos[taskKey] || taskAfterPhotos[taskKey].length === 0)) {
          missingPhotos.push(`"${room.name} - ${taskObj.text}" requires an AFTER photo`);
        }
      }
    }
    
    if (missingPhotos.length > 0) {
      return { 
        valid: false, 
        message: `Missing required photos:\n${missingPhotos.slice(0, 3).join("\n")}${missingPhotos.length > 3 ? `\n...and ${missingPhotos.length - 3} more` : ""}`
      };
    }
    return { valid: true, message: "" };
  };

  // Reference photos are uploaded by the owner; required tasks must have at least one in task_reference_photos
  const validateRequiredReferencePhotos = (): { valid: boolean; message: string } => {
    for (const room of originalRooms) {
      for (const task of room.tasks) {
        const taskObj = normalizeTask(task);
        if (!taskObj.require_reference_photo) continue;
        const refPhotos = getTaskReferencePhotos(room.name, taskObj.text);
        if (!refPhotos || refPhotos.length === 0) {
          return {
            valid: false,
            message: tDynamic("Task not complete — reference photos still needed."),
          };
        }
      }
    }
    return { valid: true, message: "" };
  };

  const areAllTasksCompleted = (): boolean => {
    for (const room of originalRooms) {
      for (const task of room.tasks) {
        const taskText = getTaskText(task);
        if (!isTaskCompleted(room.name, taskText)) {
          return false;
        }
      }
    }
    return true;
  };

  const validateRequiredProof = (): { valid: boolean; message: string; uploaded: number; total: number } => {
    const total = requiredProofStandards.length;
    if (total === 0) return { valid: true, message: "", uploaded: 0, total: 0 };
    let uploaded = 0;
    for (const s of requiredProofStandards) {
      const entrySatisfied = isProofStandardFullySatisfied(
        s,
        checklistRoomNames,
        entryProofUrlsRaw,
        entryFunctionalPassIds,
      );
      const hasMedia = (proofByStandardId[s.id]?.length ?? 0) > 0;
      if (entrySatisfied || hasMedia) uploaded++;
    }
    const valid = uploaded === total;
    return {
      valid,
      message: valid ? "" : tDynamic("Task not complete. Proof photos/videos still required."),
      uploaded,
      total,
    };
  };

  const uploadProofForStandard = useCallback(
    async (standardId: string, files: File[]) => {
      if (!propertyId || !taskId || files.length === 0) return;
      setProofUploadingStandardId(standardId);
      try {
        const urls: string[] = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
          const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 40);
          const path = `standards_proof/${propertyId}/${taskId}/${standardId}/${Date.now()}_${i}_${safeName}`;
          const { error } = await supabase.storage
            .from("cleaning-photos")
            .upload(path, file, { contentType: file.type, upsert: true });
          if (error) throw error;
          const { data } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
          urls.push(data.publicUrl);
        }
        setProofByStandardId((prev) => ({
          ...prev,
          [standardId]: [...(prev[standardId] ?? []), ...urls],
        }));
      } catch (e) {
        toast({
          title: tDynamic("Upload failed"),
          description: (e as Error).message,
          variant: "destructive",
        });
      } finally {
        setProofUploadingStandardId(null);
      }
    },
    [propertyId, taskId, tDynamic, toast]
  );

  const handleSubmitClick = () => {
    // GUARD: Prevent double submission - if already submitting, ignore
    if (isSubmitting) {
      console.warn("[CleaningTab] Submit blocked - already submitting");
      return;
    }

    // GUARD: Prevent submitting completed tasks - if readOnly, block submission
    if (readOnly) {
      console.warn("[CleaningTab] Submit blocked - task is read-only (completed)");
      toast({ 
        title: t("error"), 
        description: t("cleaningTaskAlreadyCompleted"), 
        variant: "destructive" 
      });
      return;
    }

    const validation = validateRequiredPhotos();
    if (!validation.valid) {
      // Expand rooms / scroll to first task missing required proof photos
      const roomIndicesWithMissing = new Set<number>();
      let firstMissingRoomIndex: number | null = null;
      let firstMissingTaskKey: string | null = null;
      originalRooms.forEach((room, roomIndex) => {
        for (const task of room.tasks) {
          const taskObj = normalizeTask(task);
          const taskKey = getTaskKey(room.name, taskObj.text);
          const missingBefore = taskObj.require_before_photo && (!taskBeforePhotos[taskKey] || taskBeforePhotos[taskKey].length === 0);
          const missingAfter = taskObj.require_after_photo && (!taskAfterPhotos[taskKey] || taskAfterPhotos[taskKey].length === 0);
          if (missingBefore || missingAfter) {
            roomIndicesWithMissing.add(roomIndex);
            if (firstMissingRoomIndex === null) firstMissingRoomIndex = roomIndex;
            if (firstMissingTaskKey === null) firstMissingTaskKey = taskKey;
          }
        }
      });
      setCleaningMainTab("checklist");
      setExpandedRooms((prev) => {
        const next = new Set(prev);
        roomIndicesWithMissing.forEach((i) => next.add(i));
        return next;
      });
      if (firstMissingTaskKey) {
        const scrollId =
          checklistViewMode === "workflow"
            ? `workflow-task-${firstMissingTaskKey}`
            : firstMissingRoomIndex !== null
              ? `cleaning-room-${firstMissingRoomIndex}`
              : null;
        if (scrollId) {
          setTimeout(() => {
            document.getElementById(scrollId)?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 80);
        }
      }
      toast({
        title: t("missingRequiredPhotos"),
        description: validation.message,
        variant: "destructive"
      });
      return;
    }

    const refPhotoValidation = validateRequiredReferencePhotos();
    if (!refPhotoValidation.valid) {
      toast({ 
        title: refPhotoValidation.message, 
        variant: "destructive" 
      });
      setShowReferencePhotosRequired(true);
      setTimeout(() => {
        referencePhotosAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return;
    }

    const proofValidation = validateRequiredProof();
    if (!proofValidation.valid) {
      toast({
        title: proofValidation.message,
        description: `${proofValidation.uploaded}/${proofValidation.total} ${tDynamic("proof items uploaded")}`,
        variant: "destructive",
      });
      return;
    }

    setShowReferencePhotosRequired(false);
    if (areAllTasksCompleted()) {
      handleSubmit();
    } else {
      setShowIncompleteWarning(true);
    }
  };

  const handleSubmit = async () => {
    // GUARD: Prevent double submission - check again inside async function
    if (isSubmitting) {
      console.warn("[CleaningTab] Submit blocked - already submitting (double-check)");
      return;
    }

    // GUARD: Prevent submitting completed tasks
    if (readOnly) {
      console.warn("[CleaningTab] Submit blocked - task is read-only (double-check)");
      return;
    }

    const refPhotoValidation = validateRequiredReferencePhotos();
    if (!refPhotoValidation.valid) {
      toast({ title: refPhotoValidation.message, variant: "destructive" });
      setShowReferencePhotosRequired(true);
      setTimeout(() => {
        referencePhotosAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return;
    }

    const proofValidation = validateRequiredProof();
    if (!proofValidation.valid) {
      toast({
        title: proofValidation.message,
        description: `${proofValidation.uploaded}/${proofValidation.total} ${tDynamic("proof items uploaded")}`,
        variant: "destructive",
      });
      return;
    }

    setShowIncompleteWarning(false);
    setShowReferencePhotosRequired(false);
    setIsSubmitting(true);
    try {
      const submissionId = generateUUID();

      // Upload room photos for legacy compatibility (storage only; submission row not required)
      const photos = await buildLegacyPhotosArray(submissionId);

      // Upload issue photos (storage only)
      const issues = await uploadIssuePhotos(submissionId);

      // Upload maintenance task reports
      await uploadMaintenanceReports();

      // Build tasks_status
      const tasksStatus: { room_name: string; task_text: string; completed: boolean }[] = [];
      let totalTasks = 0;
      let completedCount = 0;

      for (const room of originalRooms) {
        for (const task of room.tasks) {
          const taskText = getTaskText(task);
          const isCompleted = isTaskCompleted(room.name, taskText);
          tasksStatus.push({
            room_name: room.name,
            task_text: taskText,
            completed: isCompleted,
          });
          totalTasks++;
          if (isCompleted) completedCount++;
        }
      }

      const completionPercentage = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

      // Create submission row first (required for task_submission_photos FK)
      const submitParams = {
        propertyId,
        notes: notes.trim(),
        issues,
        photos,
        tasksStatus,
        completionPercentage,
        submissionId, // Pass the pre-generated ID
      };
      if (submitCleaningAsOwnerOrTeam) {
        await submitCleaningAsOwnerOrTeam.mutateAsync(submitParams);
      } else {
        await submitCleaning.mutateAsync({
          ...submitParams,
          checklistId: checklist?.id || null,
          cleaningTaskId: taskId ?? null,
        });
      }

      // If photos were autosaved to a draft submission, copy those persisted rows to the final submission.
      if (draftSubmissionId && draftSubmissionId !== submissionId) {
        const { data: draftPhotos, error: draftPhotosError } = await supabase
          .from("task_submission_photos")
          .select("room_name, task_text, photo_url, type, display_order")
          .eq("submission_id", draftSubmissionId);
        if (draftPhotosError) {
          console.error("[CleaningTab] Failed to load autosaved draft task photos:", draftPhotosError);
        } else if (draftPhotos && draftPhotos.length > 0) {
          const payload = draftPhotos.map((p) => ({
            submission_id: submissionId,
            room_name: p.room_name,
            task_text: p.task_text,
            photo_url: p.photo_url,
            type: p.type,
            display_order: p.display_order,
          }));
          const { error: copyError } = await supabase.from("task_submission_photos").insert(payload);
          if (copyError) {
            console.error("[CleaningTab] Failed to copy autosaved draft task photos:", copyError);
          }
        }
      }

      // Now insert task-level before/after photos (submission must exist for FK)
      await uploadPhotosToTable(submissionId);

      void (async () => {
        try {
          const { data: subPhotos } = await supabase
            .from("task_submission_photos")
            .select("room_name, task_text, photo_url, type")
            .eq("submission_id", submissionId);
          if (!subPhotos?.length || !taskReferencePhotos) return;
          const pairs: { room_name: string; task_text: string; reference_url: string; after_url: string }[] = [];
          for (const room of originalRooms) {
            for (const task of room.tasks || []) {
              if (!isCleaningTask(task)) continue;
              const tt = getTaskText(task);
              if (!getCleaningSettings(task).require_reference_photo) continue;
              const refs = getPhotosForTask(taskReferencePhotos, room.name, tt);
              const refUrl = refs[0]?.photo_url;
              const afterRow = subPhotos.find(
                (p) =>
                  p.type === "after" &&
                  (p.room_name ?? "").trim() === (room.name ?? "").trim() &&
                  (p.task_text ?? "").trim() === tt.trim()
              );
              if (refUrl && afterRow?.photo_url) {
                pairs.push({
                  room_name: room.name,
                  task_text: tt,
                  reference_url: refUrl,
                  after_url: afterRow.photo_url,
                });
              }
            }
          }
          const result = await compareCleaningPhotosForSubmission(pairs, {
            propertyId,
            cleaningTaskId: taskId ?? null,
          });
          if (result.ok && result.comparisons?.length) {
            await supabase
              .from("cleaning_submissions")
              .update({
                ai_reconciliation: {
                  comparisons: result.comparisons,
                  generated_at: new Date().toISOString(),
                } as unknown as Json,
              })
              .eq("id", submissionId);
          }
        } catch (e) {
          console.warn("[CleaningTab] AI photo reconciliation skipped", e);
        }
      })();

      // Save standard proof uploads (proof required for this turnover)
      for (const [standardId, urls] of Object.entries(proofByStandardId)) {
        if (urls?.length) {
          await upsertStandardProofUpload.mutateAsync({
            submission_id: submissionId,
            cleaning_task_id: taskId ?? null,
            standard_id: standardId,
            proof_media_urls: urls,
          });
        }
      }

      // Fire issue notification immediately if any damage/issues were reported
      if (issues.length > 0) {
        sendMaintenanceIssuesEmail({
          propertyId,
          taskId: taskId ?? null,
          cleanerName: cleanerRecord?.full_name ?? null,
          issues,
        }).catch(() => {});
      }

      const hasDamageReport = issues.length > 0;

      // Advance state machine after successful submission (before toast)
      // This allows the parent to show success banner and handle state transition
      // GUARD: Only advance if callback exists and we're not read-only
      if (onChecklistSubmitted && !readOnly) {
        onChecklistSubmitted();
      }
      
      // Show toast (default duration is 5 seconds - ensures message is visible)
      toast({
        title: t("success"),
        description: hasDamageReport
          ? "Damage reported. Your photo has been saved as evidence."
          : t("cleaningReportSubmitted"),
      });
      
      // Delete temporary progress after successful submission
      deleteProgress();
      
      // Clean up preview URLs (memory cleanup - doesn't affect form state)
      Object.values(taskBeforePhotos).flat().forEach(p => URL.revokeObjectURL(p.previewUrl));
      Object.values(taskAfterPhotos).flat().forEach(p => URL.revokeObjectURL(p.previewUrl));
      Object.values(maintenancePhotos).flat().forEach(p => URL.revokeObjectURL(p.previewUrl));
      
      // STATE RESET PREVENTED: The following lines were causing the checklist form to reset after submission.
      // Commented out to preserve local checklist answers after submit.
      // If you need to reset the form, uncomment these lines:
      // setCompletedTasks(new Set()); // RESET: Clears all completed task checkboxes
      // setNotes(""); // RESET: Clears notes textarea
      // setRoomPhotos({}); // RESET: Clears room-level photo uploads
      // setTaskBeforePhotos({}); // RESET: Clears task before photos
      // setTaskAfterPhotos({}); // RESET: Clears task after photos
      // setMaintenanceResponses({}); // RESET: Clears maintenance task responses
      // setMaintenanceNotes({}); // RESET: Clears maintenance task notes
      // setMaintenancePhotos({}); // RESET: Clears maintenance task photos
      // setRoomIssues({}); // RESET: Clears room issue reports
    } catch (error) {
      console.error("[CleaningTab] Submission error:", error);
      const rawMessage = error instanceof Error
        ? error.message
        : (error && typeof error === "object" && "message" in error)
          ? String((error as { message: unknown }).message)
          : "";
      // Don't show raw DB/server errors to the user (e.g. "function gen_random_bytes(integer) does not exist")
      const isServerError = /function|does not exist|42883|PGRST/.test(rawMessage);
      const errorMessage = isServerError
        ? t("failedToSubmitReport")
        : rawMessage || t("failedToSubmitReport");
      toast({
        title: t("error"),
        description: errorMessage,
        variant: "destructive",
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <p className="text-muted-foreground">{tDynamic("Loading...")}</p>;
  }

  return (
    <div className="relative">
      <div className={`space-y-4 ${entryGateActive ? "pointer-events-none select-none opacity-[0.35]" : ""}`}>
        {!checklist ? (
          <Card>
            <CardContent className="py-6">
              <p className="text-muted-foreground">{tDynamic("No checklist assigned to this property yet.")}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="overflow-hidden rounded-3xl border-[color:var(--pv-line,hsl(var(--border)))] shadow-md">
              <CardHeader ref={headerRef} className="space-y-3 border-b border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/15 pb-3">
                {rooms.length > 0 ? (
                  <div className="flex items-start gap-3" dir={isRtl ? "rtl" : "ltr"}>
                    <div className="relative h-[52px] w-[52px] shrink-0">
                      <CleaningProgressRing pct={checklistHeaderStats.pct} />
                      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums text-foreground">
                        {checklistHeaderStats.pct}%
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--pv-teal-hover,#0b8e83)]">
                        {tDynamic("Cleaning checklist")}
                      </p>
                      <CardTitle className="text-xl font-bold leading-tight tracking-tight text-foreground sm:text-2xl">
                        {displayChecklistName}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground leading-snug">
                        {tDynamic("After photos show the unit after you clean.")}
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {checklistHeaderStats.completed} / {checklistHeaderStats.total} {tDynamic("done")}
                        {!hideInlineElapsedLabel && timerStartedAt && cleaningElapsedLabel ? (
                          <>
                            {" · "}
                            <span
                              className="inline-flex items-center gap-1 font-medium text-foreground"
                              style={{ color: TEAL_ACCENT }}
                            >
                              <span
                                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--pv-teal,#0fa89a)]"
                                aria-hidden
                              />
                              <Clock className="inline h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                              {cleaningElapsedLabel}
                            </span>
                          </>
                        ) : !hideInlineElapsedLabel ? (
                          <>
                            {" · "}
                            <span>{tDynamic("Not started")}</span>
                          </>
                        ) : null}
                      </p>
                      <div className="flex w-full max-w-md items-center gap-2.5">
                        <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted/80">
                          <div
                            className={cn("h-full rounded-full transition-all duration-300", checklistHeaderStats.pct === 100 && "bg-green-500")}
                            style={{
                              width: `${checklistHeaderStats.pct}%`,
                              backgroundColor: checklistHeaderStats.pct === 100 ? undefined : TEAL_ACCENT,
                            }}
                          />
                        </div>
                        <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                          {checklistHeaderStats.pct}%
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1" dir={isRtl ? "rtl" : "ltr"}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--pv-teal-hover,#0b8e83)]">
                      {tDynamic("Cleaning checklist")}
                    </p>
                    <CardTitle className="text-xl font-bold sm:text-2xl">{displayChecklistName}</CardTitle>
                    <p className="text-sm text-muted-foreground">{tDynamic("After photos show the unit after you clean.")}</p>
                  </div>
                )}
                <div
                  className="flex gap-0 border-b border-[color:var(--pv-line,hsl(var(--border)))] -mx-6 px-6"
                  role="tablist"
                  aria-label={tDynamic("Checklist sections")}
                  dir={isRtl ? "rtl" : "ltr"}
                >
                  {(
                    [
                      ["checklist", tDynamic("Checklist"), `${checklistHeaderStats.completed} / ${checklistHeaderStats.total}`] as const,
                      ["issues", tDynamic("Issues"), issuesTabCount > 0 ? String(issuesTabCount) : ""] as const,
                    ] as const
                  ).map(([id, label, count]) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={cleaningMainTab === id}
                      className={cn(
                        "relative flex-1 px-2 py-2.5 text-center text-sm font-medium transition-[color,transform] duration-150 sm:py-3",
                        cleaningMainTab === id
                          ? "text-[color:var(--pv-teal,#0fa89a)]"
                          : "text-muted-foreground hover:text-[color:var(--pv-ink)] active:scale-[0.98]"
                      )}
                      onClick={() => setCleaningMainTab(id)}
                    >
                      <span className="block truncate">
                        {label}
                        {count !== "" ? (
                          <span
                            className={cn(
                              "ml-1 tabular-nums",
                              cleaningMainTab === id ? "text-[color:var(--pv-teal,#0fa89a)]" : "text-muted-foreground"
                            )}
                          >
                            {count}
                          </span>
                        ) : null}
                      </span>
                      {cleaningMainTab === id ? (
                        <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-[color:var(--pv-teal,#0fa89a)]" />
                      ) : null}
                    </button>
                  ))}
                </div>
              </CardHeader>
            <CardContent className="space-y-5 p-4 sm:p-6">
              {cleaningMainTab === "checklist" ? (
              <>
              {showReferencePhotosRequired && (
                <Alert
                  ref={referencePhotosAlertRef}
                  variant="destructive"
                  className="rounded-xl ring-2 ring-amber-500 ring-offset-2"
                >
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{tDynamic("Task not complete — reference photos still needed.")}</AlertTitle>
                  <AlertDescription>
                    {tDynamic("The task will stay In progress until the owner adds reference photos for required tasks in Checklists.")}
                  </AlertDescription>
                </Alert>
              )}
              {proofStandardsForCleaningList.length > 0 && (
                <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                  <h3 className="text-sm font-semibold">{tDynamic("Proof required for this turnover")}</h3>
                  <p className="text-xs text-muted-foreground">
                    {requiredProofStandardsStillInList.length > 0
                      ? tDynamic("Upload proof for each item below to complete the task.")
                      : requiredProofStandards.length > 0
                        ? tDynamic("Required proof was captured at entry. Optional items are below.")
                        : tDynamic("Optional proof items — upload if you want to document condition.")}
                  </p>
                  {requiredProofStandards.length > 0 && (
                    <p className="text-xs text-primary">
                      {validateRequiredProof().uploaded}/{validateRequiredProof().total}{" "}
                      {tDynamic("proof items uploaded")}
                    </p>
                  )}
                  <ul className="space-y-2">
                    {proofStandardsForCleaningList.map((s) => {
                      const urls = proofByStandardId[s.id] ?? [];
                      const hasProof = urls.length > 0;
                      const isRequired = s.required_every_turnover;
                      const isUploading = proofUploadingStandardId === s.id;
                      const fileInputId = `proof-${s.id}`;
                      return (
                        <li
                          key={s.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded border p-3 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <span className="font-medium">{s.title}</span>
                            {s.room && (
                              <span className="text-muted-foreground ml-1">({s.room})</span>
                            )}
                            <div className="mt-1.5">
                              <UnitItemProofTypeBadge type={s.type} t={t} className="text-xs normal-case" />
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className={`inline-flex items-center gap-1 text-xs ${
                                  hasProof ? "text-green-600" : isRequired ? "text-destructive" : "text-muted-foreground"
                                }`}
                              >
                                {hasProof ? (
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                ) : (
                                  <XCircle className="h-3.5 w-3.5" />
                                )}
                                {hasProof ? tDynamic("Uploaded") : isRequired ? tDynamic("Missing") : tDynamic("Optional")}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setProofReferenceModal(s)}
                            >
                              {tDynamic("View reference")}
                            </Button>
                            {s.reference_youtube_url?.trim() ? (
                              <Button type="button" variant="outline" size="sm" asChild>
                                <a
                                  href={s.reference_youtube_url.trim()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="mr-1 h-3.5 w-3.5 shrink-0" aria-hidden />
                                  {t("unitItemsWatchYoutubeDemo")}
                                </a>
                              </Button>
                            ) : null}
                            {!readOnly && (
                              <>
                                <input
                                  id={fileInputId}
                                  type="file"
                                  accept="image/*,video/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => {
                                    const files = e.target.files;
                                    if (files?.length) {
                                      uploadProofForStandard(s.id, Array.from(files));
                                      e.target.value = "";
                                    }
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant={hasProof ? "secondary" : "default"}
                                  size="sm"
                                  disabled={isUploading}
                                  aria-busy={isUploading}
                                  onClick={() => document.getElementById(fileInputId)?.click()}
                                >
                                  {isUploading ? (
                                    <Loader2 className="mr-1 h-4 w-4 shrink-0 animate-spin" aria-hidden />
                                  ) : s.type === "proof_video" ? (
                                    <Video className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                                  ) : s.type === "functional_check" ? (
                                    <ClipboardCheck className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                                  ) : (
                                    <Camera className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                                  )}
                                  {tDynamic("Upload proof")}
                                </Button>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {/* View mode: Room view (default) vs Workflow view */}
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:pt-0.5">
                  {tDynamic("View")}
                </span>
                <div
                  className="flex w-full max-w-md rounded-full border border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/40 p-1 sm:w-auto sm:min-w-[280px]"
                  role="group"
                  aria-label={tDynamic("Checklist view mode")}
                >
                  <button
                    type="button"
                    className={cn(
                      "min-h-[40px] flex-1 rounded-full px-3 py-2 text-sm font-semibold transition-all duration-200 active:scale-[0.98]",
                      checklistViewMode === "room"
                        ? "bg-[color:var(--pv-teal,#0fa89a)] text-white shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    onClick={() => setChecklistViewMode("room")}
                  >
                    {tDynamic("By room")}
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "min-h-[40px] flex-1 rounded-full px-3 py-2 text-sm font-semibold transition-all duration-200 active:scale-[0.98]",
                      checklistViewMode === "workflow"
                        ? "bg-[color:var(--pv-teal,#0fa89a)] text-white shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    onClick={() => {
                      const uid = cleanerRecord?.id ?? "anon";
                      const k = `workflow_view_explainer_v1_${uid}`;
                      if (typeof localStorage !== "undefined" && !localStorage.getItem(k)) {
                        setWorkflowExplainerOpen(true);
                        localStorage.setItem(k, "1");
                      }
                      setChecklistViewMode("workflow");
                    }}
                  >
                    {tDynamic("By workflow")}
                  </button>
                </div>
              </div>

              {cleaningMainTab === "checklist" && nextFocusTaskKey && !areAllTasksCompleted() && (
                <Alert className="rounded-xl border-amber-400/55 bg-amber-50/90 text-amber-950 dark:border-amber-500/35 dark:bg-amber-950/25 dark:text-amber-50">
                  <Info className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                  <AlertTitle className="text-amber-900 dark:text-amber-100">
                    {tDynamic("Keep going")}
                  </AlertTitle>
                  <AlertDescription className="text-amber-900/90 dark:text-amber-100/90">
                    {tDynamic("Some tasks still need to be completed. Tap the next highlighted task to continue.")}
                  </AlertDescription>
                </Alert>
              )}

              {/* Room view: filter by cleaning category */}
              {checklistViewMode === "room" && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">{tDynamic("Cleaning category")}</span>
                  <Select
                    value={cleaningCategoryFilter}
                    onValueChange={(v) => setCleaningCategoryFilter(v as CleaningCategory | "all")}
                  >
                    <SelectTrigger className="h-9 w-full max-w-xs rounded-full border-[color:var(--pv-line,hsl(var(--border)))] sm:w-[200px]">
                      <SelectValue placeholder={tDynamic("All")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{tDynamic("All")}</SelectItem>
                      {(Object.keys(CLEANING_CATEGORY_LABELS) as CleaningCategory[]).map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {CLEANING_CATEGORY_LABELS[cat]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {checklistViewMode === "workflow" && workflowFilteredEntries.length > 0 && workflowFilter !== "all" && (
                <p className="text-sm text-muted-foreground">
                  {(() => {
                    const tot = workflowFilteredEntries.length;
                    const done = workflowFilteredEntries.filter((e) =>
                      isTaskCompleted(e.roomName, getTaskText(e.task))
                    ).length;
                    const label =
                      workflowFilter === "laundry"
                        ? tDynamic("Laundry")
                        : CLEANING_CATEGORY_LABELS[workflowFilter as CleaningCategory];
                    return `${done}/${tot} ${tDynamic("tasks in")} ${label}`;
                  })()}
                </p>
              )}

              {checklistViewMode === "workflow" && (
                <>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={cn(
                      "h-9 rounded-full border px-3 text-sm font-semibold transition-all duration-150 active:scale-[0.98]",
                      workflowFilter === "all"
                        ? "border-[color:var(--pv-teal,#0fa89a)] bg-[color:var(--pv-teal-soft,rgba(15,168,154,0.12))] text-[color:var(--pv-teal-press,#0f766e)] shadow-sm"
                        : "border-[color:var(--pv-line,hsl(var(--border)))] bg-background text-foreground hover:bg-muted/50"
                    )}
                    onClick={() => setWorkflowFilter("all")}
                  >
                    {tDynamic("All")}
                  </button>
                  {WORKFLOW_VIEW_CATEGORY_KEYS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={cn(
                        "h-9 rounded-full border px-3 text-sm font-semibold transition-all duration-150 active:scale-[0.98]",
                        workflowFilter === c
                          ? "border-[color:var(--pv-teal,#0fa89a)] bg-[color:var(--pv-teal,#0fa89a)] text-white shadow-sm"
                          : "border-[color:var(--pv-line,hsl(var(--border)))] bg-background text-foreground hover:bg-muted/50"
                      )}
                      onClick={() => setWorkflowFilter(c)}
                    >
                      {c === "laundry" ? tDynamic("Laundry") : CLEANING_CATEGORY_LABELS[c]}
                    </button>
                  ))}
                </div>
                {workflowFilter !== "all" && nextWorkflowCategory && (
                  <div className="flex justify-end pt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-9 rounded-full border-[color:var(--pv-teal,#0fa89a)]/40 bg-background px-4 text-[color:var(--pv-teal-press,#0f766e)] shadow-sm transition-transform duration-150 hover:bg-[color:var(--pv-teal-soft,rgba(15,168,154,0.1))] active:scale-[0.98]"
                      onClick={() => {
                        setWorkflowFilter(nextWorkflowCategory);
                        requestAnimationFrame(() => scrollRootToTop());
                      }}
                    >
                      {tDynamic("Next")}:{" "}
                      {nextWorkflowCategory === "laundry"
                        ? tDynamic("Laundry")
                        : CLEANING_CATEGORY_LABELS[nextWorkflowCategory]}
                    </Button>
                  </div>
                )}
                {workflowFilter !== "all" &&
                  workflowFilteredEntries.length > 0 &&
                  workflowFilteredEntries.every((e) => isTaskCompleted(e.roomName, getTaskText(e.task))) && (
                    <div
                      className="flex items-center justify-center gap-2 rounded-lg border border-[color:var(--pv-teal,#0fa89a)]/28 bg-[color:var(--pv-teal-soft,rgba(230,244,242,0.9))] px-3 py-2 text-center text-sm font-medium text-[color:var(--pv-teal-hover,#0b8e83)]"
                      role="status"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                      {tDynamic("All tasks in this category are done.")}
                    </div>
                  )}
                </>
              )}

              {guideEntries.length > 0 && (
                <div className="mb-4">
                  <PropertyGuideCard
                    entries={guideEntries}
                    roomName={null}
                    defaultOpen={false}
                  />
                </div>
              )}
              {(!rooms || rooms.length === 0) ? (
                <div
                  className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/10 px-4 py-10 text-center"
                  role="status"
                >
                  <ClipboardList className="h-10 w-10 text-[color:var(--pv-ink-3,hsl(var(--muted-foreground)))] opacity-50" aria-hidden />
                  <p className="text-sm text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">{tDynamic("No rooms in this checklist.")}</p>
                </div>
              ) : checklistViewMode === "room" && cleaningCategoryFilter !== "all" && filteredRoomsForDisplay.length === 0 ? (
                <div
                  className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/10 px-4 py-10 text-center"
                  role="status"
                >
                  <ClipboardList className="h-10 w-10 text-[color:var(--pv-ink-3,hsl(var(--muted-foreground)))] opacity-50" aria-hidden />
                  <p className="text-sm text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">{tDynamic("No tasks in this category.")}</p>
                </div>
              ) : checklistViewMode === "workflow" ? (
                <div className="space-y-3">
                  {workflowFilteredEntries.length === 0 ? (
                    <div
                      className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/10 px-4 py-10 text-center"
                      role="status"
                    >
                      <ClipboardList className="h-10 w-10 text-[color:var(--pv-ink-3,hsl(var(--muted-foreground)))] opacity-50" aria-hidden />
                      <p className="text-sm text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">{tDynamic("No tasks in this filter.")}</p>
                    </div>
                  ) : (
                    workflowFilteredEntries.map(({ roomName, roomIndex, task, taskIndex, subcategory }) => {
                      const originalRoom = originalRooms[roomIndex];
                      const originalRoomName = originalRoom?.name ?? roomName;
                      const taskObj = normalizeTask(task);
                      const origTask = originalRoom?.tasks?.[taskIndex];
                      const origTaskObj = origTask ? normalizeTask(origTask as Task) : taskObj;
                      const originalTaskText = origTaskObj.text;
                      const displayTaskText = taskObj.text;
                      const taskKey = getTaskKey(originalRoomName, originalTaskText);
                      const taskRefPhotos = getTaskReferencePhotos(originalRoomName, originalTaskText);
                      const hasRefPhotos = taskRefPhotos.length > 0;
                      const showRefPhoto = !!origTaskObj.require_reference_photo && hasRefPhotos;
                      const showBeforeUpload = !!origTaskObj.require_before_photo;
                      const showAfterUpload = !!origTaskObj.require_after_photo;
                      const subcategoryLabel =
                        subcategory === "laundry" || subcategory === "beds_linens"
                          ? tDynamic("Laundry")
                          : CLEANING_CATEGORY_LABELS[subcategory as CleaningCategory];
                      return (
                        <div
                          key={taskKey}
                          id={`workflow-task-${taskKey}`}
                          className="scroll-mt-24 overflow-hidden rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-card shadow-sm"
                        >
                          <div className="flex flex-wrap items-center gap-2 border-b border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/25 px-3 py-2">
                            <span className="text-xs font-medium text-muted-foreground">{originalRoomName}</span>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs font-semibold text-[color:var(--pv-teal-hover,#0b8e83)]">
                              {subcategoryLabel}
                            </span>
                          </div>
                          <ChecklistTaskRow
                            task={task}
                            taskIndex={taskIndex}
                            roomIndex={roomIndex}
                            originalRoom={originalRoom}
                            originalRoomName={originalRoomName}
                            displayTaskText={displayTaskText}
                            taskKey={taskKey}
                            readOnly={readOnly}
                            isTaskCompleted={isTaskCompleted}
                            onToggleTask={toggleTask}
                            taskBeforePhotos={taskBeforePhotos}
                            taskAfterPhotos={taskAfterPhotos}
                            maintenanceResponses={maintenanceResponses}
                            maintenanceNotes={maintenanceNotes}
                            maintenancePhotos={maintenancePhotos}
                            quickCheckVerified={quickCheckVerified}
                            quickCheckPhotos={quickCheckPhotos}
                            taskRefPhotos={taskRefPhotos}
                            showRefPhoto={showRefPhoto}
                            showBeforeUpload={showBeforeUpload}
                            showAfterUpload={showAfterUpload}
                            onMaintenanceResponseChange={handleMaintenanceResponseChange}
                            onMaintenanceNotesChange={handleMaintenanceNotesChange}
                            onMaintenanceAddPhotos={handleMaintenanceAddPhotos}
                            onMaintenanceRemovePhoto={handleMaintenanceRemovePhoto}
                            onQuickCheckVerify={handleQuickCheckVerify}
                            onQuickCheckAddPhotos={handleQuickCheckAddPhotos}
                            onQuickCheckRemovePhoto={handleQuickCheckRemovePhoto}
                            onAddBeforePhotos={handleAddBeforePhotos}
                            onRemoveBeforePhoto={handleRemoveBeforePhoto}
                            onAddAfterPhotos={handleAddAfterPhotos}
                            onRemoveAfterPhoto={handleRemoveAfterPhoto}
                            tDynamic={tDynamic}
                            guideEntries={guideEntries}
                            noRowDivider
                            isNextTask={nextFocusTaskKey === taskKey}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                filteredRoomsForDisplay.map(({ roomIndex, room, filteredRoom }, displayIdx) => {
                    const originalRoom = originalRooms[roomIndex];
                    const roomKey = (originalRoom?.name ?? room.name) || `room-${roomIndex}`;
                    const isExpanded = expandedRooms.has(roomIndex);
                    const toggleRoomExpanded = () => {
                      setExpandedRooms((prev) => {
                        const next = new Set(prev);
                        if (next.has(roomIndex)) next.delete(roomIndex);
                        else next.add(roomIndex);
                        return next;
                      });
                    };
                    // Determine if this room is the next incomplete room and supply the
                    // localized name of the room that follows it so we can show a "Next room" cue.
                    let nextRoomLocalizedName: string | null = null;
                    let onJumpToNextRoom: (() => void) | undefined;
                    for (let i = displayIdx + 1; i < filteredRoomsForDisplay.length; i++) {
                      const candidate = filteredRoomsForDisplay[i];
                      const candidateOriginal = originalRooms[candidate.roomIndex];
                      const candidateName = candidateOriginal?.name ?? candidate.room.name;
                      const candidateTasks = candidateOriginal?.tasks ?? [];
                      const allDone =
                        candidateTasks.length > 0 &&
                        candidateTasks.every((t) =>
                          completedTasks.has(getTaskKey(candidateName, getTaskText(t)))
                        );
                      if (!allDone) {
                        nextRoomLocalizedName = candidate.room.name;
                        const targetRoomIndex = candidate.roomIndex;
                        onJumpToNextRoom = () => {
                          setExpandedRooms((prev) => {
                            const next = new Set(prev);
                            next.add(targetRoomIndex);
                            return next;
                          });
                          requestAnimationFrame(() => {
                            const el = document.getElementById(`cleaning-room-${targetRoomIndex}`);
                            el?.scrollIntoView({ behavior: "smooth", block: "start" });
                          });
                        };
                        break;
                      }
                    }
                    return (
                      <div key={roomKey} id={`cleaning-room-${roomIndex}`} className="scroll-mt-24">
                      <ChecklistRoomRow
                        room={filteredRoom}
                        originalRoom={originalRoom}
                        roomIndex={roomIndex}
                        isExpanded={isExpanded}
                        onToggleExpand={toggleRoomExpanded}
                        dir={isRtl ? "rtl" : "ltr"}
                        readOnly={readOnly}
                        getTaskKey={getTaskKey}
                        getTaskReferencePhotos={getTaskReferencePhotos}
                        getReferencePhotoForRoom={getReferencePhotoForRoom}
                        isTaskCompleted={isTaskCompleted}
                        onToggleTask={toggleTask}
                        taskBeforePhotos={taskBeforePhotos}
                        taskAfterPhotos={taskAfterPhotos}
                        maintenanceResponses={maintenanceResponses}
                        maintenanceNotes={maintenanceNotes}
                        maintenancePhotos={maintenancePhotos}
                        quickCheckVerified={quickCheckVerified}
                        quickCheckPhotos={quickCheckPhotos}
                        onMaintenanceResponseChange={handleMaintenanceResponseChange}
                        onMaintenanceNotesChange={handleMaintenanceNotesChange}
                        onMaintenanceAddPhotos={handleMaintenanceAddPhotos}
                        onMaintenanceRemovePhoto={handleMaintenanceRemovePhoto}
                        onQuickCheckVerify={handleQuickCheckVerify}
                        onQuickCheckAddPhotos={handleQuickCheckAddPhotos}
                        onQuickCheckRemovePhoto={handleQuickCheckRemovePhoto}
                        onAddBeforePhotos={handleAddBeforePhotos}
                        onRemoveBeforePhoto={handleRemoveBeforePhoto}
                        onAddAfterPhotos={handleAddAfterPhotos}
                        onRemoveAfterPhoto={handleRemoveAfterPhoto}
                        roomPhotos={roomPhotos}
                        roomIssues={roomIssues}
                        onPhotoSelect={handlePhotoSelect}
                        onToggleRoomIssue={toggleRoomIssue}
                        onUpdateRoomIssueDescription={updateRoomIssueDescription}
                        onUpdateRoomIssuePhoto={updateRoomIssuePhoto}
                        onUpdateRoomIssueSeverity={updateRoomIssueSeverity}
                        tDynamic={tDynamic}
                        guideEntries={guideEntries}
                        roomInventoryItems={roomItemsByInvRoomId[getInventoryRoomId(originalRoom ?? filteredRoom)] ?? []}
                        onUpdateInventoryItem={handleUpdateInventoryItem}
                        onRequestOrder={openRequestSheet}
                        onPurchased={openPurchaseSheet}
                        hasRequestOrderForItem={hasRequestOrderForItem}
                        hideRoomInventoryPanel={hideInlineRoomInventory}
                        hideRoomPhotoAndIssueSection
                        nextFocusTaskKey={nextFocusTaskKey}
                        nextRoomLocalizedName={nextRoomLocalizedName}
                        onJumpToNextRoom={onJumpToNextRoom}
                      />
                      </div>
                    );
                  })
              )}
              </>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {tDynamic("Report damage or issues by room. Same options as on the Checklist tab.")}
                  </p>
                  <div className="space-y-4">
                    {filteredRoomsForDisplay.map(({ roomIndex, room }) => {
                      const originalRoom = originalRooms[roomIndex];
                      const originalRoomName = originalRoom?.name ?? room.name;
                      const localizedName = room.name;
                      return (
                        <div key={originalRoomName} className="space-y-3 rounded-xl border border-border/70 bg-card p-4 shadow-sm">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{localizedName}</p>
                          <div>
                            <Label className="mb-2 block text-sm font-medium">{tDynamic("Any damages in this room?")}</Label>
                            <select
                              className="flex h-12 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-base"
                              value={roomIssues[originalRoomName]?.hasIssue ? "yes" : "no"}
                              onChange={(e) => toggleRoomIssue(originalRoomName, e.target.value === "yes")}
                              disabled={readOnly}
                            >
                              <option value="no">{tDynamic("No")}</option>
                              <option value="yes">{tDynamic("Yes")}</option>
                            </select>
                          </div>
                          {roomIssues[originalRoomName]?.hasIssue && (
                            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                              <h4 className="flex items-center gap-2 text-sm font-medium text-amber-800">
                                <AlertCircle className="h-4 w-4" />
                                {tDynamic("Damage Details")}
                              </h4>
                              <div>
                                <Label className="mb-1 block text-xs text-amber-700">{tDynamic("Severity")}</Label>
                                <select
                                  className="flex h-9 w-full rounded-md border border-amber-300 bg-white px-3 text-sm"
                                  value={roomIssues[originalRoomName]?.severity ?? "medium"}
                                  onChange={(e) =>
                                    updateRoomIssueSeverity(originalRoomName, e.target.value as IssueSeverity)
                                  }
                                  disabled={readOnly}
                                >
                                  <option value="low">{tDynamic("Low")}</option>
                                  <option value="medium">{tDynamic("Medium")}</option>
                                  <option value="high">{tDynamic("High")}</option>
                                  <option value="urgent">{tDynamic("Urgent")}</option>
                                </select>
                              </div>
                              <div>
                                <Label className="mb-1 block text-xs text-amber-700">{tDynamic("Describe the damage...")}</Label>
                                <Textarea
                                  placeholder={tDynamic("Describe the damage...")}
                                  value={roomIssues[originalRoomName]?.description ?? ""}
                                  onChange={(e) => updateRoomIssueDescription(originalRoomName, e.target.value)}
                                  rows={2}
                                  className="bg-white text-base"
                                  disabled={readOnly}
                                />
                              </div>
                              <div>
                                <Label className="mb-1 block text-xs text-amber-700">{tDynamic("Photo (optional)")}</Label>
                                <label className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white hover:bg-amber-50">
                                  <Camera className="h-4 w-4 text-amber-600" />
                                  <span className="text-sm text-amber-700">
                                    {roomIssues[originalRoomName]?.photo
                                      ? `${tDynamic("Add Photo")} ✓`
                                      : tDynamic("Add Photo")}
                                  </span>
                                  <Input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                      updateRoomIssuePhoto(originalRoomName, e.target.files?.[0] ?? null)
                                    }
                                    className="hidden"
                                    disabled={readOnly}
                                  />
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Proof reference media modal */}
      <Dialog open={!!proofReferenceModal} onOpenChange={() => setProofReferenceModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{proofReferenceModal?.title}</DialogTitle>
            <DialogDescription className="sr-only">{t("unitItemsModalSrOnly")}</DialogDescription>
          </DialogHeader>
          {proofReferenceModal ? (
            <ProofStandardHostDetailContent standard={proofReferenceModal} t={t} />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Room Gallery Modal */}
      <Dialog open={!!galleryRoom} onOpenChange={() => setGalleryRoom(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              {galleryRoom?.name} - {tDynamic("Reference Photos")}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {tDynamic("Reference photos for this room and its tasks.")}
            </DialogDescription>
          </DialogHeader>
          
          {galleryRoom && (
            <div className="space-y-4">
              {(() => {
                const cover = getReferencePhotoForRoom(galleryRoom.name);
                return cover ? (
                  <div className="border-b pb-4">
                    <p className="text-sm font-medium mb-2 text-muted-foreground">{tDynamic("Room Cover Photo")}</p>
                    <TaskReferencePhotoStrip photos={[{ id: cover.id, photo_url: cover.photo_url }]} />
                  </div>
                ) : null;
              })()}

              <div>
                <p className="text-sm font-medium mb-3">{tDynamic("Task Reference Photos")}</p>
                
                {galleryRoom.tasks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{tDynamic("No tasks in this room.")}</p>
                ) : (
                  <div className="space-y-3">
                    {galleryRoom.tasks.map((task, index) => {
                      const taskText = getTaskText(task);
                      const taskPhotos = getTaskReferencePhotos(galleryRoom.name, taskText);
                      
                      return (
                        <div key={index} className="border rounded-lg p-3">
                          <p className="text-sm font-medium mb-2">{taskText}</p>
                          
                          {taskPhotos.length > 0 ? (
                            <TaskReferencePhotoStrip photos={taskPhotos} />
                          ) : (
                            <p className="text-xs text-muted-foreground italic">{tDynamic("No reference photo")}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Notes Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{tDynamic("Notes")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder={tDynamic("Any additional notes about this cleaning...")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            disabled={readOnly}
          />
        </CardContent>
      </Card>

      {/* Submit Button */}
      {!readOnly && (
        <div className="sticky bottom-0 z-10 -mx-4 mt-6 space-y-2 border-t border-[color:var(--pv-line,hsl(var(--border)))] bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-md supports-[backdrop-filter]:bg-background/85 md:static md:mx-0 md:border-0 md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-none md:supports-[backdrop-filter]:md:bg-transparent">
          {!areAllTasksCompleted() && (
            <div
              className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-900 shadow-sm dark:border-red-900/40 dark:bg-red-950/35 dark:text-red-50"
              role="status"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-300" aria-hidden />
              <span>{tDynamic("Some tasks still need to be completed.")}</span>
            </div>
          )}
          <Button
            onClick={handleSubmitClick}
            disabled={isSubmitting}
            className="h-12 w-full rounded-2xl text-base font-semibold shadow-md"
          >
            <Send className="mr-2 h-4 w-4" />
            {isSubmitting
              ? tDynamic("Saving...")
              : areAllTasksCompleted()
                ? tDynamic("Complete Cleaning")
                : `${tDynamic("Continue")} (${checklistHeaderStats.completed}/${checklistHeaderStats.total})`}
          </Button>
        </div>
      )}

      {/* Pre-cleaning: room vs workflow (once per task) */}
      <Dialog open={preCleaningViewIntroOpen} onOpenChange={setPreCleaningViewIntroOpen}>
        <DialogContent className="border-t-[3px] border-t-[color:var(--pv-teal,hsl(var(--primary)))] pt-5">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--pv-ink,hsl(var(--foreground)))]">
              {tDynamic("How do you want to work?")}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {tDynamic("Choose room view or workflow view for this cleaning.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
            <div className="rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] divide-y divide-[color:var(--pv-line,hsl(var(--border)))] overflow-hidden">
              <div className="px-4 py-3 space-y-0.5">
                <p className="font-semibold text-[color:var(--pv-ink,hsl(var(--foreground)))]">{tDynamic("By room")}</p>
                <p className="text-xs">{tDynamic("Work through each room top to bottom. Good if you clean one space at a time.")}</p>
              </div>
              <div className="px-4 py-3 space-y-0.5">
                <p className="font-semibold text-[color:var(--pv-ink,hsl(var(--foreground)))]">{tDynamic("By workflow")}</p>
                <p className="text-xs">{tDynamic("Groups all laundry together, all floors together, etc. Good if you do one type of task across every room before moving to the next.")}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="secondary"
              className="flex-1 transition-transform duration-150 active:scale-[0.98]"
              onClick={() => {
                setChecklistViewMode("room");
                setPreCleaningViewIntroOpen(false);
              }}
            >
              {tDynamic("By room")}
            </Button>
            <Button
              type="button"
              className="flex-1 transition-transform duration-150 active:scale-[0.98]"
              onClick={() => {
                setChecklistViewMode("workflow");
                setPreCleaningViewIntroOpen(false);
              }}
            >
              {tDynamic("By workflow")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workflow view explainer (first time) */}
      <Dialog open={workflowExplainerOpen} onOpenChange={setWorkflowExplainerOpen}>
        <DialogContent className="border-t-[3px] border-t-[color:var(--pv-teal,hsl(var(--primary)))] pt-5">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--pv-ink,hsl(var(--foreground)))]">{tDynamic("Workflow view")}</DialogTitle>
            <DialogDescription className="sr-only">
              {tDynamic("Explanation of workflow view versus room view.")}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
            {tDynamic(
              "Tasks are grouped by type — all laundry in one list, all floors in another, and so on. Tap a category chip to focus on one job at a time. Reference photos from the host show inline when available."
            )}
          </p>
          <Button className="w-full" onClick={() => setWorkflowExplainerOpen(false)}>
            {tDynamic("Got it")}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Incomplete Tasks Warning Dialog */}
      <AlertDialog open={showIncompleteWarning} onOpenChange={setShowIncompleteWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tDynamic("Some tasks are not completed")}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>{tDynamic("You haven't checked off all tasks in this checklist.")}</p>
              <p>{tDynamic("Are you sure you want to finish this cleaning?")}</p>
              <p className="text-xs text-muted-foreground">
                {tDynamic("Uncompleted tasks will be marked as incomplete in the report.")}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tDynamic("Back")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit}>
              {tDynamic("Yes")}, {tDynamic("Done")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inline inventory: request/purchase sheet (shared across room panels) */}
      <InventoryItemSheet
        item={selectedItemForSheet}
        open={inventorySheetOpen}
        onOpenChange={(open) => {
          if (!open) setInventorySheetInitialMode("idle");
          setInventorySheetOpen(open);
        }}
        initialActionMode={inventorySheetInitialMode}
        existingRequestOrder={
          selectedItemForSheet ? requestOrderByItemIdForSheet.get(selectedItemForSheet.id) ?? null : null
        }
        propertyId={propertyId}
        cleanerId={cleanerRecord?.id ?? ""}
        reimbursementMode={reimbursementMode}
        cleaningTaskId={taskId ?? undefined}
      />
      {entryGateActive && (
        <div className="pointer-events-auto absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-lg border bg-background/90 p-4 text-center backdrop-blur-sm shadow-sm">
          <p className="text-sm font-medium">{tDynamic("Finish arrival & unit check first")}</p>
          <p className="text-xs text-muted-foreground max-w-sm">
            {tDynamic(
              "Cleaning photos here are for after you clean. Use the arrival step for how the guest left the unit."
            )}
          </p>
        </div>
      )}
    </div>
  </div>
);
};

export default CleaningTab;
